import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAssessmentAnalysis,
  getAssessmentStatus,
} from '../services/api';
import type {
  AnalysisResponse,
  AssessmentStatusResponse,
  AttributeResult,
} from '../types/api';
import { BRAND_CODE } from '../config/branding';
import { buildSpaSections } from '../components/spa/spaSections';
import { FormattedText, cleanTooltip } from '../components/FormattedText';
import { StatusChip } from '../components/StatusChip';
/* Sprint 5 client feedback 2026-07-07 (FE Ticket 1) — reuse the shared
   TruncatedCell for every text-heavy report cell so long LLM prose
   clamps to 2 lines with a "View" modal. Replaces the ad-hoc
   .slice(0, N) truncations that appeared in §06/§08/§09/§11. */
import { TruncatedCell } from '../components/TruncatedCell';
/* Sprint 5 client feedback 2026-07-11 (Iter6 #1) — every source
   citation in every DD table now routes through the canonical
   SourceChip helper (utils/sourceChip.tsx) so the display format
   matches SPA + Risk tables exactly. */
import { SourceChip } from '../utils/sourceChip';
import { parseSectorSubsector } from '../utils/subsectorParser';

/* Sprint 5 client feedback 2026-07-11 (Iter4 S4) — Reject / Approve
   added to the STATUS_BADGES map so the overall-status pill on both
   DD and SPA reports renders in the correct semantic colour instead
   of falling through to badge-neutral. Reject → red fail badge,
   Approve → green pass badge. */
const STATUS_BADGES: Record<string, string> = {
  Pass: 'badge-pass',
  Approve: 'badge-pass',
  Watch: 'badge-watch',
  Conditional: 'badge-watch',
  Fail: 'badge-fail',
  Reject: 'badge-fail',
};

const SUBTYPE_LABEL: Record<string, string> = {
  InvestmentMemo: 'Investment Memo',
  FinancialDD: 'Financial DD Report',
  TechnicalDD: 'Technical DD Report',
  SPA: 'Share Purchase Agreement',
  SHA: 'Shareholders Agreement',
  Escrow: 'Escrow Agreement',
};

interface ReportSection {
  id: string;
  num: string;
  title: string;
  body: React.ReactNode;
}

/* Sprint 5 client feedback 2026-07-11 (Iter4 D2/S2) — DD + SPA report
   header now surfaces the date PLUS the local time so reviewers can
   tell repeated same-day runs apart. 24-hour format for legibility;
   en-GB locale so days precede months. */
function formatDateWithTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return (
    d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    + ' '
    + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
  );
}

// Format a free-form date string ("6/6/23", "7/30/2028", "2023-06-06") as
// "DD MMM YYYY". Used for derivation inputs whose values come from the LLM
// as whatever the document quoted, not as ISO. Falls back to the raw string
// when no parser handles it.
function formatFreeDate(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '—';
  const s = String(raw).trim();

  // ISO YYYY-MM-DD
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }

  // M/D/Y or D/M/Y. Heuristic: if first segment > 12 it must be D/M;
  // otherwise default to M/D (US-style IC memos).
  const slash = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/.exec(s);
  if (slash) {
    let m = Number(slash[1]);
    let d = Number(slash[2]);
    let y = Number(slash[3]);
    if (m > 12 && d <= 12) {
      [m, d] = [d, m];
    }
    if (y < 100) y = y >= 69 ? 1900 + y : 2000 + y;
    const dt = new Date(y, m - 1, d);
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }

  return s;
}

/**
 * Format IRR for the Section 01 metric card — numeric value only, no
 * qualifier prefix. Model returns value=29 (number) or value="29" (string).
 * The "projected" / "target" / "base_case" qualifier moves to the
 * subline so the headline number reads cleanly.
 */
function formatIrr(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') return `${value}%`;
  const s = String(value).trim();
  if (!s) return '—';
  // Already has a % sign? Use as-is. Otherwise append.
  return s.endsWith('%') ? s : `${s}%`;
}

/**
 * Format Equity Check for the Section 01 metric card — show ONLY the
 * investor commitment amount, not the whole "Investor commitment: X |
 * Total round: Y" string the deal_size handler returns. The total
 * round size belongs on the subline / detail card, not the headline.
 *
 * Accepts the deal_size value string in any of these shapes:
 *   "Investor commitment: US$ 30M | Total round: US$ 350M – US$ 380M"
 *   "Investor commitment: US$ 30M"
 *   "Total round: US$ 350M"
 *   "US$ 30M"
 * and pulls out the first dollar amount.
 */
function formatEquityCheck(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  const s = String(value).trim();
  if (!s) return '—';
  // Try the labelled form first.
  const labelled = s.match(/Investor commitment:\s*([^|]+?)(?:\s*\||$)/i);
  if (labelled) return labelled[1].trim();
  // Otherwise pull the first currency amount we see.
  const amount = s.match(/(?:US\$|USD|EUR|GBP|\$|€|£)\s*[\d.,]+\s*[MBKmbk]?(?:\s*[–—-]\s*(?:US\$|USD|EUR|GBP|\$|€|£)?\s*[\d.,]+\s*[MBKmbk]?)?/);
  if (amount) return amount[0].trim();
  /* Sprint 5 client feedback 2026-06-22 — when the value is a bare
     number ("30.0000", 30, "30") we previously returned it verbatim
     so the card read "30.0000" or, when JS stringified the number
     differently, sometimes nothing. Convert bare numbers to the
     canonical "$30M" format the user asked for, stripping trailing-
     zero noise. */
  const numeric = Number(s.replace(/,/g, ''));
  if (!Number.isNaN(numeric) && Number.isFinite(numeric)) {
    const intPart = Math.trunc(numeric);
    const decimals = numeric === intPart
      ? ''
      : numeric.toFixed(2).replace(/\.?0+$/, '').replace(/^\d+/, '');
    return `$${intPart}${decimals}M`;
  }
  return s;
}

function findAttr(attrs: AttributeResult[], key: string): AttributeResult | undefined {
  return attrs.find((a) => a.attribute_key === key);
}

/* Pick the tier pill colour in Section 01 metric tiles — one colour per
   step of the risk-appetite ladder, so the same tier always reads the
   same across cards:
     Opportunistic / Aggressive → orange   (highest appetite)
     Value Add                  → amber
     Regular / Core / Core-Plus → neutral slate
     Conservative               → blue
     out-of-range               → red
   "Verified" (Equity Check) uses tier-verified and is set inline.

   `matched` can be a comma-joined multi-tier match (e.g. "Core / Core-Plus,
   Value Add, Opportunistic"), so test by substring, highest tier first. */
function tierPillClass(matched: string | null, withinRange: boolean | null | undefined): string {
  if (withinRange === false) return 'tier-fail';
  if (!matched) return 'tier-neutral';
  const m = matched.toLowerCase();
  if (m.includes('opportunistic')) return 'tier-opportunistic';
  if (m.includes('aggressive')) return 'tier-aggressive';
  if (m.includes('value add')) return 'tier-watch';
  if (m.includes('conservative')) return 'tier-conservative';
  return 'tier-neutral';
}

/* Map matched-bracket name to a progress-bar fill width + colour
   class. The design hardcodes 80% / 45% for IRR / HP — we pick
   per-tier widths so the bar reads as "position within range":
     Conservative ~ 25%, Regular/Value Add ~ 55%, Aggressive ~ 85%,
     Opportunistic ~ 95%. Out-of-range fills 100% in fail-red. */
function tierFillSpec(matched: string | null, withinRange: boolean | null | undefined): { width: string; cls: string } {
  if (withinRange === false) return { width: '100%', cls: 'fill-fail' };
  if (!matched) return { width: '0%', cls: 'fill-neutral' };
  const m = matched.toLowerCase();
  if (m === 'conservative') return { width: '25%', cls: 'fill-neutral' };
  if (m === 'regular' || m === 'value add') return { width: '55%', cls: '' };
  if (m === 'aggressive') return { width: '85%', cls: '' };
  if (m === 'opportunistic') return { width: '95%', cls: '' };
  return { width: '50%', cls: '' };
}

function SourceLine({ attr }: { attr: AttributeResult }) {
  // Sprint 5 client feedback 2026-07-10 (DD.docx §2.1 / §3.5) — suppress
  // the raw extracted-content dump. The client wants only Source + Page
  // + Type; the page-title / section-heading enrichment is a BE
  // follow-up (needs Qdrant chunk-heading metadata surfaced on
  // AttributeResult). Until that lands, showing the quoted extract
  // was worse than nothing — it dumped ugly pipe-delimited table rows
  // as if they were a section title.
  if (!attr.source_document && attr.source_page == null) return null;
  return (
    <div style={{
      marginTop: 'var(--s3)', padding: 'var(--s3)',
      background: 'var(--surface-alt, rgba(0,0,0,0.02))',
      border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
      fontSize: '0.78rem',
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s3)', alignItems: 'baseline' }}>
        {attr.source_document && (
          <span><span className="t-muted">Source:</span> <strong>{attr.source_document}</strong></span>
        )}
        {attr.source_page != null && (
          <span><span className="t-muted">Page:</span> <strong>{attr.source_page}</strong></span>
        )}
        {attr.value_type && (
          <span><span className="t-muted">Type:</span> <strong>{attr.value_type}</strong></span>
        )}
      </div>
    </div>
  );
}

interface CellModal {
  title: string;
  // Iter7 #6 — allow ReactNode so mitigation modals can pass a
  // FormattedText JSX tree (rendered markdown) instead of the raw
  // string. String body still works unchanged for existing callers.
  body: ReactNode;
}

/* Sprint 5 client feedback 2026-07-11 (Iter6 #1) — replaced with a
   shared helper (see utils/sourceChip.tsx) so every DD / SPA / Risk
   table uses the same short-label mapping. Kept the legacy
   DOC_LABEL_SHORT object as a thin adapter so the existing call
   sites that read it directly continue to work; new call sites
   should import `SourceChip` from utils/sourceChip.tsx and use it
   instead of hand-rendering the pill. Names widened to match the
   Iter6 client sample (FinancialDD / TechnicalDD / ICRes) instead
   of the earlier abbreviations (FinDD / TechDD). */
const DOC_LABEL_SHORT: Record<string, string> = {
  'Investment Memo': 'InvMemo',
  'Financial DD Report': 'FinancialDD',
  'Financial DD': 'FinancialDD',
  'Technical DD Report': 'TechnicalDD',
  'Technical DD': 'TechnicalDD',
  'IC Resolution': 'ICRes',
  'IC Memo': 'ICRes',
  'Optional Document': 'Other',
};

// LevelBadge now uses the .risk-low/medium/high CSS classes (see
// index.css). The inline colour map was removed when those classes
// were ported from frontend/design/.

// Sprint 5 client feedback 2026-07-10 (DD.docx §5.7) — Flag column
// taxonomy locked to Pass / Watch / Flag. "Monitor" removed;
// deriveFlag routes the both-Medium case into Watch instead so no
// row can display the deprecated label.
const FLAG_STYLE: Record<string, { bg: string; fg: string }> = {
  Flag:    { bg: 'rgba(220, 38, 38, 0.10)',  fg: '#B91C1C' },
  Watch:   { bg: 'rgba(245, 158, 11, 0.12)', fg: '#B45309' },
  Pass:    { bg: 'rgba(34, 197, 94, 0.10)',  fg: '#15803D' },
};

// 5-level normalisation matching the IC-memo appendix matrix
// vocabulary. 'Critical'/'Almost Certain' get a tier of their own
// (deep red) so analysts can immediately see the highest-risk rows
// without misreading them as 'just High'. 'Insignificant'/'Rare'
// likewise drop below 'Minor'/'Unlikely' for visual separation.
function normaliseLevel(
  v: unknown,
): 'Critical' | 'High' | 'Medium' | 'Low' | 'Minimal' | null {
  if (v == null) return null;
  const s = String(v).trim().toLowerCase();
  // Sprint 5 client feedback 2026-07-07 (BE Issue #2) — extended the
  // synonym set so common LLM emissions ("Severe", "Catastrophic",
  // "Significant", "Probable", "Certain", "Negligible") map to a
  // canonical bucket instead of falling through to null (which used
  // to render an unstyled <span> in LevelBadge).
  if (['critical', 'almost certain', 'certain', 'extreme', 'catastrophic', 'severe'].includes(s)) return 'Critical';
  if (['high', 'major', 'likely', 'probable', 'significant'].includes(s)) return 'High';
  if (['medium', 'moderate', 'possible'].includes(s)) return 'Medium';
  if (['low', 'minor', 'unlikely'].includes(s)) return 'Low';
  if (['insignificant', 'rare', 'negligible'].includes(s)) return 'Minimal';
  return null;
}

/**
 * Sprint 5 client feedback 2026-07-07 (BE Issue #2) — the DD §5 Risk
 * Profile Impact / Likelihood columns must display exactly one label
 * from their fixed taxonomies. Prior renderer showed the RAW LLM
 * emission (e.g. "Severe"), which fell outside the taxonomy and
 * matched no variant CSS rule.
 *
 * `canonicalRiskLabel(kind, raw)` returns the canonical label for
 * the given kind:
 *   * kind='impact'     → Critical / Major / Moderate / Minor
 *                          / Insignificant  (5-tier, matches risk_enrich
 *                          _IMPACT_VOCAB)
 *   * kind='likelihood' → Almost Certain / Likely / Possible /
 *                          Unlikely / Rare
 *   * kind='generic'    → raw string with normaliseLevel bucket labels
 *                          (Critical / High / Medium / Low / Minimal)
 *
 * Returns null if the input can't be mapped; caller decides on the
 * "Unknown" fallback badge.
 */
type RiskLevelKind = 'impact' | 'likelihood' | 'generic';

function canonicalRiskLabel(
  kind: RiskLevelKind,
  raw: unknown,
): string | null {
  const bucket = normaliseLevel(raw);
  if (bucket == null) return null;
  if (kind === 'impact') {
    if (bucket === 'Critical') return 'Critical';
    if (bucket === 'High') return 'Major';
    if (bucket === 'Medium') return 'Moderate';
    if (bucket === 'Low') return 'Minor';
    if (bucket === 'Minimal') return 'Insignificant';
  } else if (kind === 'likelihood') {
    if (bucket === 'Critical') return 'Almost Certain';
    if (bucket === 'High') return 'Likely';
    if (bucket === 'Medium') return 'Possible';
    if (bucket === 'Low') return 'Unlikely';
    if (bucket === 'Minimal') return 'Rare';
  }
  return bucket;  // generic — return the bucket label as-is
}

// Risk-matrix flag rule (extended for the 5-level vocabulary):
//   Critical and/or Critical                -> Flag    (red)
//   Both High                                -> Flag    (red)
//   Both Medium                              -> Monitor (neutral)
//   At least one High/Critical               -> Watch   (amber)
//   Everything else (Low, Minimal, unknown)  -> Pass    (green)
function deriveFlag(likelihood: unknown, impact: unknown): keyof typeof FLAG_STYLE {
  const l = normaliseLevel(likelihood);
  const i = normaliseLevel(impact);
  if (l === 'Critical' && i === 'Critical') return 'Flag';
  if (l === 'High' && i === 'High') return 'Flag';
  if ((l === 'Critical' || l === 'High') && (i === 'Critical' || i === 'High')) return 'Flag';
  // Sprint 5 client feedback 2026-07-10 (DD.docx §5.7) — Monitor
  // retired. Medium×Medium now surfaces as Watch (amber) so the row
  // gets analyst attention instead of the previous neutral tint.
  if (l === 'Medium' && i === 'Medium') return 'Watch';
  if (l === 'Critical' || l === 'High' || i === 'Critical' || i === 'High') return 'Watch';
  return 'Pass';
}

/* Sprint 5 client feedback 2026-06-30 (New Issues D5) — DD §5 Risk
   Profile previously rendered only the individual rows table; the
   analyst had no aggregate severity verdict for the deal as a whole.
   Mirror the Risk Report's §5 "Overall Investment Risk" tile by
   computing the composite verdict from row likelihood × impact, and
   surface the benchmark profile (Core / Core-Plus / Value Add /
   Opportunistic) from §3 Investment Classification.

   classifyDdRow keeps consistency with deriveFlag above but resolves
   to one of the 4 severity bands rather than the 3 flag tiers, so
   the composite verdict aligns with the Risk Report's KPI strip. */
type DdSeverity = 'Critical' | 'High' | 'Medium' | 'Low';

/**
 * Sprint 5 client feedback 2026-07-07 (BE Issue #1) — Source Type
 * badge variant lookup. Prior renderer had a ternary chain that fell
 * through to the base class name "src-type" when the value_type was
 * anything other than "Stated" / "Derived" — producing the useless
 * combination class="src-type src-type" for Not Found and User Input.
 *
 * Now: full map covering every canonical value_type; anything
 * unrecognised lands on "src-unknown" (neutral) so we NEVER re-emit
 * the base class in the variant slot.
 *
 * value_type values come from AttributeResult (backend/src/services/
 * analysis/handlers/base.py — "Stated" | "Derived" | "User Input" |
 * "Not Found").
 */
const _SOURCE_TYPE_VARIANT_MAP: Record<string, string> = {
  'stated':     'src-extracted',
  'extracted':  'src-extracted',
  'derived':    'src-derived',
  'not found':  'src-notfound',
  'not_found':  'src-notfound',
  'notfound':   'src-notfound',
  'user input': 'src-userinput',
  'user_input': 'src-userinput',
  'userinput':  'src-userinput',
};

function _sourceTypeVariant(valueType: string | null | undefined): string {
  const key = (valueType ?? '').toString().trim().toLowerCase();
  if (!key) return 'src-unknown';
  return _SOURCE_TYPE_VARIANT_MAP[key] ?? 'src-unknown';
}

/**
 * Sprint 5 client feedback 2026-07-07 (BE Issue #3) — Key Assumptions
 * §7 Status column: {Matched, Partial, Not Matched, Not Supported}.
 * Prior renderer had a ternary that fell to '' for "Not Supported",
 * so the wrapper used a plain-text `t-muted t-xs` render — visually
 * broken next to the tinted sibling pills.
 *
 * Map covers the four canonical statuses + the historical variance-
 * band synonyms (In-Range → Matched, Watch → Partial, Aggressive /
 * Conservative → Not Matched). Anything unmapped lands on
 * `risk-unknown` (added by the earlier §5 Impact/Likelihood fix).
 */
const _ASSUMPTION_STATUS_VARIANT_MAP: Record<string, string> = {
  'matched':       'risk-low',
  'in-range':      'risk-low',
  'in_range':      'risk-low',
  'in range':      'risk-low',
  'supported':     'risk-low',
  'partial':       'risk-medium',
  'watch':         'risk-medium',
  'not matched':   'risk-high',
  'not_matched':   'risk-high',
  'notmatched':    'risk-high',
  'aggressive':    'risk-high',
  'conservative':  'risk-high',
  'not supported': 'risk-neutral',
  'not_supported': 'risk-neutral',
  'notsupported':  'risk-neutral',
};

function _assumptionStatusVariant(status: string | null | undefined): string {
  const key = (status ?? '').toString().trim().toLowerCase();
  if (!key) return 'risk-unknown';
  return _ASSUMPTION_STATUS_VARIANT_MAP[key] ?? 'risk-unknown';
}

function _classifyDdRow(row: Record<string, unknown>): DdSeverity {
  const l = normaliseLevel(row['likelihood']);
  const i = normaliseLevel(row['impact']);
  if (l === 'Critical' || i === 'Critical') return 'Critical';
  if (l === 'High' && i === 'High') return 'Critical';
  if (l === 'High' || i === 'High') return 'High';
  if (l === 'Medium' && i === 'Medium') return 'Medium';
  if (l === 'Medium' || i === 'Medium') return 'Medium';
  return 'Low';
}

function _compositeRiskVerdict(rows: Record<string, unknown>[]): DdSeverity {
  // Worst-band wins so the composite reflects the most severe row,
  // matching how the Risk Report aggregates.
  let worst: DdSeverity = 'Low';
  const rank: Record<DdSeverity, number> = { Critical: 4, High: 3, Medium: 2, Low: 1 };
  for (const r of rows) {
    if (!r || typeof r !== 'object') continue;
    const s = _classifyDdRow(r);
    if (rank[s] > rank[worst]) worst = s;
  }
  return worst;
}

/* Sprint 5 client feedback 2026-06-30 — Overall Investment Risk uses
   the benchmark's 3-tier vocabulary {High, Medium, Low}. The
   composite verdict above can resolve to Critical (per-row severity)
   but at the aggregate level Critical collapses to High because the
   benchmark doesn't define a Critical tier. */
function _toBenchmarkTier(v: DdSeverity): 'High' | 'Medium' | 'Low' {
  return v === 'Critical' ? 'High' : (v as 'High' | 'Medium' | 'Low');
}

const _INV_TYPE_TO_PROFILE: Record<string, string> = {
  // From DD.yaml: investment_type catalog → benchmark profile category
  LocalDirectInvestment: 'Opportunistic',
  Corporate: 'Core',
  PrivateEquityFunds: 'Value Add',
  RealEstateFunds: 'Core-Plus',
  RealEstateDirect: 'Core-Plus',
};

function _benchmarkProfile(invKey: string | null | undefined): string {
  if (!invKey) return '—';
  return _INV_TYPE_TO_PROFILE[invKey] ?? invKey;
}

/* Sprint 5 client feedback 2026-07-10 (DD.docx §5.2) — insert a space
   between CamelCase tokens so backend values like "DirectInvestment"
   render as "Direct Investment" in the Benchmark Applied panel. Values
   already spaced (e.g. "Core / Core-Plus", "Value Add") pass through
   unchanged. */
function _humaniseProfile(v: string | null | undefined): string {
  if (!v) return '—';
  const s = String(v).trim();
  if (!s || /\s/.test(s)) return s;
  return s.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function OverallRiskAssessmentPanel({
  rows,
  invTypeKey,
  summary,
}: {
  rows: Record<string, unknown>[];
  invTypeKey: string | null | undefined;
  summary?: { overall_rating?: string | null; benchmark_profile?: string | null } | null;
}): JSX.Element | null {
  const dictRows = rows.filter((r) => r != null && typeof r === 'object' && !Array.isArray(r));
  if (dictRows.length === 0) return null;
  // Sprint 5 2026-06-30 — prefer the API-computed summary so DD page +
  // Risk page + PDF all show the SAME values. Fall back to local
  // computation only when the API hasn't populated the summary (e.g.
  // legacy report_json without the enriched summary block).
  // Critical collapses to High at the aggregate (3-tier benchmark).
  const verdict = (summary?.overall_rating as 'High' | 'Medium' | 'Low' | undefined)
    ?? _toBenchmarkTier(_compositeRiskVerdict(dictRows));
  const profile = summary?.benchmark_profile || _benchmarkProfile(invTypeKey);
  const verdictColor =
    verdict === 'High'     ? 'var(--fail)' :
    verdict === 'Medium'   ? 'var(--watch)' :
                             'var(--pass)';
  const verdictBg =
    verdict === 'High'     ? 'var(--fail-bg)' :
    verdict === 'Medium'   ? 'var(--watch-bg)' :
                             'var(--pass-bg)';
  return (
    <div
      className="card card-sm"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 'var(--s4)',
        marginBottom: 'var(--s4)',
        borderLeft: `4px solid ${verdictColor}`,
        background: verdictBg,
      }}
    >
      <div>
        {/* Sprint 5 client feedback 2026-07-10 (DD.docx §5.3) — info
            tooltip explains the overall risk rating is an AI-calculated
            composite of likelihood × impact × mitigation across all
            identified risks, benchmarked against the investment
            profile. */}
        <p
          className="t-label"
          style={{ margin: 0, marginBottom: 4 }}
          data-tip="AI-calculated composite based on validation of all identified risks, likelihood, impact, mitigation effectiveness, and benchmark evaluation."
        >
          Overall Investment Risk
          <span style={{ marginLeft: 4, opacity: 0.5, cursor: 'help' }}>ⓘ</span>
        </p>
        <div style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.8rem',
          fontWeight: 600,
          color: verdictColor,
          lineHeight: 1.1,
        }}>{verdict}</div>
        {/* Sprint 5 client feedback 2026-07-10 (DD.docx §5.1) —
            "composite verdict" descriptor removed at client's request;
            the verdict word itself already conveys the meaning. */}
        {/* Sprint 5 client feedback 2026-07-11 (Issues.docx #8) — when
            the overall verdict is High (or Medium with any Flag rows),
            surface a one-line AI commentary listing the flagged risk
            categories. Gives the analyst a quick "why" beside the
            headline verdict without opening §5's per-row table. */}
        {(() => {
          const flaggedCats: string[] = [];
          const watchCats: string[] = [];
          for (const row of dictRows) {
            const likelihoodNorm = _normalizeLikelihoodFE(row['likelihood']);
            const flag = deriveFlag(likelihoodNorm || row['likelihood'], row['impact']);
            const cat = String(row['risk'] ?? '').trim();
            if (!cat) continue;
            if (flag === 'Flag') flaggedCats.push(cat);
            else if (flag === 'Watch') watchCats.push(cat);
          }
          if (!flaggedCats.length && !watchCats.length) return null;
          const lead = verdict === 'High'
            ? 'Overall Risk is High due to'
            : verdict === 'Medium'
              ? 'Overall Risk is Medium driven by'
              : 'Contributing risks';
          const cats = flaggedCats.length ? flaggedCats : watchCats;
          const shown = cats.slice(0, 3);
          const more = cats.length - shown.length;
          return (
            <p
              className="t-xs"
              style={{
                margin: '6px 0 0',
                color: 'var(--text-sec)',
                lineHeight: 1.45,
              }}
              data-tip={cats.join(', ')}
            >
              {lead}: {shown.join(', ')}
              {more > 0 ? ` + ${more} more` : ''}.
            </p>
          );
        })()}
      </div>
      <div>
        {/* Sprint 5 client feedback 2026-07-10 (DD.docx §5.2 + Iter1 §5) —
            heading renamed to "Benchmark Applied" for parity with the §3
            card. Iter1 adds an ⓘ tooltip naming the source policy doc
            (OIA Investment Policy Final-Approved 2024) so analysts see
            where the benchmark profile came from. */}
        <p
          className="t-label"
          style={{ margin: 0, marginBottom: 4 }}
          data-tip="Benchmark profile derived from OIA Investment Policy Final-Approved 2024"
        >
          Benchmark Applied <span style={{ opacity: 0.5, cursor: 'help' }}>ⓘ</span>
        </p>
        <div style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.4rem',
          fontWeight: 600,
          lineHeight: 1.2,
        }}>{_humaniseProfile(profile)}</div>
        <p className="t-xs t-muted" style={{ margin: 0, marginTop: 4 }}>from Section 3 Investment Classification</p>
      </div>
      {/* Sprint 5 client feedback 2026-07-11 (Issues.docx #5) — the
          Overall Investment Risk summary card had a large empty right
          gutter. Fill it with a compact Flag/Watch/Pass count strip
          that gives analysts an at-a-glance risk composition without
          scrolling to the §5 per-row table. Values derived from the
          same deriveFlag() pipeline that drives the table itself so
          the totals never disagree. */}
      {(() => {
        let flagN = 0, watchN = 0, passN = 0;
        for (const row of dictRows) {
          const likelihoodNorm = _normalizeLikelihoodFE(row['likelihood']);
          const flag = deriveFlag(likelihoodNorm || row['likelihood'], row['impact']);
          if (flag === 'Flag') flagN += 1;
          else if (flag === 'Watch') watchN += 1;
          else passN += 1;
        }
        const total = flagN + watchN + passN;
        if (total === 0) return null;
        const pct = (n: number) => (n / total) * 100;
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            minWidth: 200,
          }}>
            <p className="t-label" style={{ margin: 0, marginBottom: 4 }}>
              Risk Composition
            </p>
            {[
              { label: 'Flag',  n: flagN,  colour: 'var(--fail)' },
              { label: 'Watch', n: watchN, colour: 'var(--watch)' },
              { label: 'Pass',  n: passN,  colour: 'var(--pass)' },
            ].map((r) => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.75rem' }}>
                <span style={{ minWidth: 46, color: 'var(--text-sec)', fontWeight: 600 }}>{r.label}</span>
                <div
                  aria-hidden="true"
                  style={{
                    flex: 1,
                    height: 8,
                    borderRadius: 4,
                    background: 'var(--surface-alt)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${pct(r.n)}%`,
                      height: '100%',
                      background: r.colour,
                      transition: 'width 0.24s ease',
                    }}
                  />
                </div>
                <span
                  className="t-mono"
                  style={{ minWidth: 20, textAlign: 'right', color: 'var(--text)' }}
                >
                  {r.n}
                </span>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

function LevelBadge({
  value,
  kind = 'generic',
}: {
  value: unknown;
  kind?: RiskLevelKind;
}) {
  if (value == null || String(value).trim() === '') return <span className="t-muted">—</span>;
  const raw = String(value);
  const level = normaliseLevel(raw);
  const cls = level === 'Critical' ? 'risk-critical'
    : level === 'High' ? 'risk-high'
    : level === 'Medium' ? 'risk-medium'
    : level === 'Low' ? 'risk-low'
    : level === 'Minimal' ? 'risk-minimal'
    : 'risk-unknown';
  // Sprint 5 client feedback 2026-07-07 (BE Issue #2) — when the
  // caller specifies a kind ('impact' | 'likelihood'), the DISPLAY
  // label is normalised to that column's fixed taxonomy. Raw LLM
  // outputs like "Severe" now render as "Critical" with the correct
  // pill styling. When no canonical mapping exists, we keep the raw
  // label but apply risk-unknown so the badge is never unstyled.
  //
  // Also: if a warning log helps triage future LLM synonyms, log
  // once at DEBUG level (guarded to avoid a per-cell console spam).
  const label = level != null ? (canonicalRiskLabel(kind, raw) ?? raw) : raw;
  if (level == null && typeof console !== 'undefined' && console.debug) {
    console.debug('[LevelBadge] unmapped value, using risk-unknown fallback:', raw);
  }
  return <span className={cls}>{label}</span>;
}

function SourceBox({ doc, page }: { doc?: unknown; page?: unknown }) {
  /* Sprint 5 client feedback 2026-07-11 (Iter6 #1) — thin adapter
     over the canonical SourceChip helper. Preserved for backward
     compat with call sites that pass `unknown` typed values;
     coerces safely and routes to the shared component so this
     renders identically to every other Source pill in the app. */
  const docStr = doc != null ? String(doc) : null;
  const pageStr = page != null ? String(page) : null;
  return <SourceChip doc={docStr} page={pageStr} />;
}

/* Sprint 5 client feedback 2026-06-24 — mirror the DOCX-side
   cleanups into the DD report HTML render so the on-screen view
   matches the exported PDF. Three shared helpers:

   * `_normalizeLikelihoodFE` — map TechDD's binary High/Low onto
     the 5-tier scale used by FinDD rows. Same mapping as
     dd_template.py:_normalize_likelihood.
   * `_cleanMitigationFE` — strip "I\d+ Rationale:" /
     "Remaining risk:" / bare "Rationale:" leak prefixes and blank
     cells whose residual text reads as residual-risk rather than
     control. Mirrors dd_template.py:_clean_mitigation_cell.
   * `_normalizeImpactFE` — pass-through (impact vocabulary is
     already consistent on the DD side; helper kept symmetric so
     the table call site reads cleanly). */
const _LIKELIHOOD_MAP_FE: Record<string, string> = {
  high: 'Likely',
  'very high': 'Almost Certain',
  low: 'Unlikely',
  'very low': 'Rare',
  medium: 'Possible',
  moderate: 'Possible',
};
function _normalizeLikelihoodFE(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  return _LIKELIHOOD_MAP_FE[s.toLowerCase()] ?? s;
}

/**
 * Sprint 5 client feedback 2026-07-02 — surgical guard for the ONE
 * Energy §5 Risk Profile bug where rows 15-20 leaked the source
 * document's internal reference IDs ("I1"..."I9") into the
 * Likelihood/Impact columns instead of proper severity labels.
 *
 * Constraint from spec: MUST be surgical. Rows 1-14 in the same
 * table AND all rows in the EH2 build already render correct
 * labels (Possible / Likely / Minor / Moderate etc.) and MUST pass
 * through unchanged.
 *
 * Rule: activate only when the trimmed value matches ^I\d+$
 * (single "I" prefix + 1+ digits, nothing else). Anything else —
 * proper severity labels, blank cells, longer strings — is
 * returned verbatim. When the pattern hits, return "—" so the raw
 * source-document index never leaks to the analyst.
 *
 * A follow-up (BE-side) can wire in the source Technical DD
 * reference table lookup + LLM re-inference the spec describes;
 * for now the display-layer guard prevents the ugly leak.
 */
function _suppressRawIndexIdFE(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw;
  const trimmed = raw.trim();
  if (!trimmed) return raw;
  if (/^I\d+$/.test(trimmed)) return '—';
  return raw;
}

const _MITIGATION_LEAK_PREFIXES_FE = [
  /^I\d+\s+Rationale:\s*/i,
  /^Remaining\s+risk:\s*/i,
  /^Rationale:\s*/i,
];
const _MITIGATION_RISK_DESCRIPTORS_FE = [
  'further downgrading',
  'further price increases',
  'further increases',
  'inability to',
  'remaining risk',
  'further delays',
];
// Sprint 5 client feedback 2026-07-11 (Iter7 #6) — LLM mitigations
// arrive with HTML entities pre-encoded (&lt; &gt; &amp; &#39; &quot;)
// because the extraction pipeline round-trips through templates that
// escape angle brackets. Decode here so downstream FormattedText sees
// the intended plain text and renders "≤4-hour response" instead of
// literal "&lt;4-hour response".
function _decodeHtmlEntitiesFE(s: string): string {
  if (!s || (!s.includes('&') && !s.includes('&#'))) return s;
  return s
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&');
}

function _cleanMitigationFE(raw: unknown): string {
  // Sprint 5 client feedback 2026-07-11 (Format Phase 2) — the mitigation
  // cell also does length-based truncation with an "…" appended. If the
  // truncator lands between the two asterisks of a **bold** pair, the
  // resulting fragment carries a lone ** that later leaks into the DOM
  // even after the shared FormattedText wraps the render. Cleaning
  // decodes entities first, then leaves markdown IN the string (so the
  // render layer can format it), but if the cell is truncated later we
  // sanitize via stripMarkdown at that point.
  let s = _decodeHtmlEntitiesFE(String(raw ?? '').trim());
  if (!s) return '';
  for (const rx of _MITIGATION_LEAK_PREFIXES_FE) {
    const m = s.match(rx);
    if (m) {
      s = s.slice(m[0].length).trim();
    }
  }
  const sLow = s.toLowerCase();
  if (_MITIGATION_RISK_DESCRIPTORS_FE.some((p) => sLow.startsWith(p))) {
    return '';
  }
  // Sprint 5 client feedback 2026-06-24 (DD/Risk.docx issue 2) —
  // mirror the BE _clean_mitigation_cell fragment filter so the
  // HTML render matches the DOCX export. Very short cells with no
  // verb-like token read as fragments and are blanked.
  if (s.length < 18) {
    const verbMarkers = [
      ' will ', ' is ', ' has ', ' have ', ' are ', ' were ',
      ' plans ', ' ensure', ' maintain', ' conduct', ' perform',
      ' implement', ' apply', ' applies', ' hedge', ' manage',
      ' review', ' monitor', ' established', ' secured', ' signed',
      ' mitigat',
    ];
    if (!verbMarkers.some((m) => sLow.includes(m))) {
      return '';
    }
  }
  // Sprint 5 client feedback 2026-07-02 — surgical truncation
  // marker per Fix 5. Cells that already end with terminal
  // punctuation (. ! ? " …) OR an intentional ellipsis pass
  // through unchanged — no regression on complete rows. Cells
  // ending mid-word (no terminal punctuation) get "…" appended
  // so the analyst sees the truncation instead of a silent cut
  // like "Company has an integra". Working rows (~99% of the
  // table) are untouched.
  const lastChar = s.charAt(s.length - 1);
  const endsWithTerminal = /[.!?"…]$/.test(s) || s.endsWith('...');
  if (!endsWithTerminal && lastChar && /[A-Za-z]/.test(lastChar)) {
    s = s.trimEnd() + '…';
  }
  // Sprint 5 client feedback 2026-07-11 (Format Phase 2) — if the text
  // still contains an ODD number of ** markers, an upstream cut left
  // an orphan behind (e.g. "Secure **anchor offtake agreeme…"). Strip
  // orphan markers only when unpaired so complete **bold** phrases
  // still render as bold via FormattedText.
  const asterixPairs = (s.match(/\*\*/g) || []).length;
  if (asterixPairs % 2 === 1) {
    s = s.replace(/\*\*/g, '');
  }
  const underPairs = (s.match(/__/g) || []).length;
  if (underPairs % 2 === 1) {
    s = s.replace(/__/g, '');
  }
  // Sprint 5 client feedback 2026-07-11 (Iter4b §5) — the mitigation
  // cell should read as a complete recommendation, not a fragment.
  // Ensure sentence-case start (uppercase first letter) and terminal
  // period when the text ends in a letter without punctuation. Skip
  // for text that already carries a truncation marker (…) or a
  // proper terminal punctuation — no regression on well-formed rows.
  if (s.length >= 2) {
    const first = s.charAt(0);
    if (/[a-z]/.test(first)) {
      s = first.toUpperCase() + s.slice(1);
    }
    const last = s.charAt(s.length - 1);
    if (/[A-Za-z0-9]/.test(last) && !s.endsWith('…')) {
      s = s + '.';
    }
  }
  return s;
}

function RiskProfileTable({ rows }: { rows: Record<string, unknown>[] }) {
  const [modal, setModal] = useState<CellModal | null>(null);
  const dictRows = (rows ?? []).filter(
    (r) => r != null && typeof r === 'object' && !Array.isArray(r),
  );
  if (!dictRows.length) return <p className="t-muted t-sm">No risk factors extracted.</p>;

  // Sprint 5 client feedback 2026-07-11 (Issues.docx #7) — group
  // Flagged rows at the top, then Watch, then Pass. Analysts scan
  // this table for the highest-risk categories first; the prior
  // insertion order (source-document order) buried Flag rows below
  // Pass rows in some deals. Stable sort preserves within-group
  // order so no additional churn is introduced.
  const _flagRank: Record<string, number> = { Flag: 0, Watch: 1, Pass: 2 };
  const _rankedRows = dictRows
    .map((row, idx) => {
      const likelihoodNorm = _normalizeLikelihoodFE(row['likelihood']);
      const flag = deriveFlag(likelihoodNorm || row['likelihood'], row['impact']);
      return { row, idx, rank: _flagRank[flag] ?? 3 };
    })
    .sort((a, b) => (a.rank - b.rank) || (a.idx - b.idx))
    .map((e) => e.row);

  // Sprint 5 client feedback 2026-07-10 (DD Step2 §5 counter-feedback)
  // — the blanket "AI Calculated" header tooltip was misleading;
  // some rows are extracted verbatim from the source document, only
  // rows the extractor couldn't populate get LLM inference. Header
  // tooltip removed; per-cell "AI Calculated" tooltip attached only
  // when the row's _ai_inferred list contains that field.
  const _FLAG_TOOLTIP = 'Pass — risk within acceptable thresholds, no significant concerns.\nWatch — moderate risk or uncertainty; monitor.\nFlag — high or critical risk requiring management attention and further review.';
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Risk Category</th>
            <th>Description</th>
            <th>Likelihood</th>
            <th>Impact</th>
            <th>Mitigation</th>
            <th>Source</th>
            <th data-tip={_FLAG_TOOLTIP}>Flag <span style={{ opacity: 0.5, cursor: 'help' }}>ⓘ</span></th>
          </tr>
        </thead>
        <tbody>
          {_rankedRows.map((row, i) => {
            const risk = String(row['risk'] ?? '');
            // Iter7 #10 — the prompt now demands complete sentences,
            // but a small tail of legacy runs still ship descriptions
            // that trail off. If the string ends with an ellipsis or
            // a mid-word cut (letter without terminal punctuation),
            // strip the dangling fragment back to the last full stop
            // so the modal renders a readable paragraph.
            const _rawDesc = row['description'] == null ? '' : String(row['description']);
            const descFull = (() => {
              const s = _decodeHtmlEntitiesFE(_rawDesc.trim());
              if (!s) return '';
              if (/[.!?"]$/.test(s) || s.endsWith('…')) return s;
              const lastTerm = Math.max(
                s.lastIndexOf('.'), s.lastIndexOf('!'), s.lastIndexOf('?'),
              );
              if (lastTerm > s.length * 0.5) {
                return s.slice(0, lastTerm + 1).trim();
              }
              return s.replace(/\.{2,}$/, '').trimEnd() + '.';
            })();
            // Sprint 5 client feedback 2026-06-24 — clean the
            // mitigation cell and normalise likelihood vocab so the
            // HTML render matches the DOCX export (see
            // dd_template.py:_clean_mitigation_cell and
            // _normalize_likelihood). Without this the page
            // surfaces raw extraction leaks like
            // "I5 Rationale: …" and mixes High/Low (TechDD) with
            // Likely/Possible (FinDD) row-by-row.
            const mitFull = _cleanMitigationFE(row['mitigation']);
            const likelihoodNorm = _normalizeLikelihoodFE(row['likelihood']);
            const flag = deriveFlag(likelihoodNorm || row['likelihood'], row['impact']);
            const flagStyle = FLAG_STYLE[flag];
            return (
              <tr key={i}>
                <td className="td-name">{risk || '—'}</td>
                <td>
                  {descFull ? (
                    <div className="td-desc-wrap">
                      {/* Format Phase 2 — render markdown / decoded entities
                          inline so **bold** in a description doesn't leak
                          the literal asterisks. */}
                      <span className="td-desc-text">
                        <FormattedText text={descFull} inline />
                      </span>
                      <button
                        type="button"
                        className="btn-view"
                        onClick={() => setModal({ title: `Description — ${risk}`, body: <FormattedText text={descFull} /> })}
                      >View</button>
                    </div>
                  ) : <span className="t-muted">—</span>}
                </td>
                {/* Sprint 5 client feedback 2026-07-11 (Iter3 §5) — the
                    prior iteration only marked AI-inferred cells; the
                    client wants BOTH sides labeled explicitly:
                    "AI Generated" when the field is in _ai_inferred,
                    "Document-derived" otherwise (the value came from
                    the source document itself). Attaching to every
                    cell so analysts can see at a glance where each
                    value came from without opening the modal. */}
                {(() => {
                  const _aiInferred = Array.isArray(row['_ai_inferred']) ? row['_ai_inferred'] as string[] : [];
                  const _aiMsg =
                    'AI Generated — inferred by the AI risk assessment engine from the risk description and category context.';
                  const _docMsg =
                    'Document-derived — value extracted directly from the source document, not AI-generated.';
                  const _lkTitle = _aiInferred.includes('likelihood') ? _aiMsg : _docMsg;
                  const _imTitle = _aiInferred.includes('impact') ? _aiMsg : _docMsg;
                  const _mtTitle = _aiInferred.includes('mitigation') ? _aiMsg : _docMsg;
                  return (
                    <>
                      <td data-tip={_lkTitle} style={{ cursor: 'help' }}><LevelBadge value={_suppressRawIndexIdFE(likelihoodNorm || row['likelihood'])} kind="likelihood" /></td>
                      <td data-tip={_imTitle} style={{ cursor: 'help' }}><LevelBadge value={_suppressRawIndexIdFE(row['impact'])} kind="impact" /></td>
                      <td data-tip={_mtTitle} style={{ cursor: 'help' }}>
                        {mitFull ? (
                          <div className="td-desc-wrap">
                            {/* Iter7 #6 — render **bold** / *italic* markdown
                                and decoded HTML entities (&lt;, &gt;) instead
                                of the raw string with visible asterisks. */}
                            <span className="td-desc-text">
                              <FormattedText text={mitFull} inline />
                            </span>
                            <button
                              type="button"
                              className="btn-view"
                              onClick={() => setModal({ title: `Mitigation — ${risk}`, body: <FormattedText text={mitFull} /> })}
                            >View</button>
                          </div>
                        ) : (
                          <span className="t-muted" data-tip="AI could not derive a mitigation for this risk from the available evidence.">Cannot calculate</span>
                        )}
                      </td>
                    </>
                  );
                })()}
                <td>
                  <SourceBox doc={row['source_document']} page={row['source_page']} />
                </td>
                <td>
                  {/* Sprint 5 client feedback 2026-07-10 (DD Step2 §5 counter-
                      feedback) — per-row tooltip explains WHY the AI
                      derived Pass/Watch/Flag for this specific
                      row (drives from likelihood × impact combo). */}
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: 999,
                      background: flagStyle.bg,
                      color: flagStyle.fg,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'help',
                    }}
                    data-tip={
                      flag === 'Flag'
                        ? `Flagged: likelihood "${likelihoodNorm || row['likelihood'] || '—'}" combined with impact "${row['impact'] || '—'}" places this row in the highest-severity quadrant.`
                        : flag === 'Watch'
                        ? `Watch: likelihood "${likelihoodNorm || row['likelihood'] || '—'}" and impact "${row['impact'] || '—'}" surface moderate exposure that warrants monitoring.`
                        : `Pass: likelihood "${likelihoodNorm || row['likelihood'] || '—'}" and impact "${row['impact'] || '—'}" fall within acceptable thresholds.`
                    }
                  >{flag}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {modal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 'var(--s4)',
          }}
          onClick={() => setModal(null)}
        >
          <div
            className="card"
            style={{
              width: 'min(640px, 92vw)',
              maxHeight: 'min(80vh, 600px)',
              display: 'flex', flexDirection: 'column',
              background: 'var(--bg)', boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header">
              <h2 className="t-h3" style={{ margin: 0 }}>{modal.title}</h2>
              <button
                type="button"
                onClick={() => setModal(null)}
                aria-label="Close"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: 'var(--muted)' }}
              >×</button>
            </div>
            <div style={{ padding: 'var(--s4)', overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
              {modal.body}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Sprint 5 client feedback 2026-07-11 (Iter4 D7 / D8 / D9 / G3) —
   Key Assumptions table extracted to a stateful component so we can
   layer pagination (client-side, 10 rows/page) on top of the
   existing rich View modal without threading useState through the
   builder function. Owns:
     * Combined Source column (Iter4 G3): "Investment Memo p.32"
     * Formatted Status header tooltip (Iter4 D7): pre-line so
       browser renders it as three paragraphs.
     * Rich View body (Iter4 D8): description + rationale + stated
       value + benchmark + variance + assessment + flag + verdict +
       consultant + citation. Same body the Iter2 §7 modal used.
     * Pagination (Iter4 D9): only renders the pager row when
       rowCount > 10, so short tables (the common case) look
       identical to before.
   Consumed by the "Key Assumptions" section builder further down. */
/* Sprint 5 client feedback 2026-07-11 (Iter4b §4) — Classification
   column header tooltip explains that each row's Core / Value Add /
   Opportunistic label comes from comparing the extracted value
   against the OIA policy benchmark. Formatted with newlines so the
   native tooltip renders as three readable paragraphs. */
const _SECTION_04_CLASSIFICATION_TOOLTIP =
  'Classification — where the extracted value sits against benchmark tiers.\n\n'
  + 'Values shown here are the AI\'s classification result from comparing the '
  + 'metric against thresholds in the OIA Investment Policy Final-Approved 2024.\n\n'
  + 'Only the highest matching tier renders when the value satisfies more than '
  + 'one bracket (Opportunistic > Value Add > Core / Core-Plus). '
  + 'Rows without a configured benchmark render "Benchmark N/A".';

// Sprint 5 client feedback 2026-07-11 (Iter7 #11) — native title
// tooltips collapse consecutive newlines in most browsers, which
// made the earlier plain-text list read as one continuous block.
// Prefix each verdict with a bullet marker (•) so the AI-verdict
// list is scannable even when line breaks are lost. Keeping the
// \n separators so browsers that DO honour them still show a
// vertically stacked list.
const _KEY_ASSUMPTIONS_STATUS_TOOLTIP =
  'Status — final AI verdict on each consultant assumption.\n\n'
  + '• Matched — the stated assumption aligns with the benchmark.\n'
  + '• Partial — the assumption partially matches; some elements diverge.\n'
  + '• Not Matched — the assumption clearly diverges from the benchmark.\n'
  + '• Not Supported — insufficient evidence in the reviewed documents to reach a verdict.';

const _ASSUMPTIONS_ROWS_PER_PAGE = 10;

function AssumptionsTable({ rows }: { rows: import('../types/api').AssumptionRow[] }) {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(rows.length / _ASSUMPTIONS_ROWS_PER_PAGE));
  const clampedPage = Math.min(page, totalPages - 1);
  const startIdx = clampedPage * _ASSUMPTIONS_ROWS_PER_PAGE;
  const endIdx = Math.min(startIdx + _ASSUMPTIONS_ROWS_PER_PAGE, rows.length);
  const pageRows = rows.slice(startIdx, endIdx);
  const showPager = rows.length > _ASSUMPTIONS_ROWS_PER_PAGE;

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Assumption</th>
            <th>Source</th>
            <th
              data-tip={_KEY_ASSUMPTIONS_STATUS_TOOLTIP}
              style={{ whiteSpace: 'pre-line' }}
            >
              Status <span style={{ opacity: 0.5, cursor: 'help' }}>ⓘ</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row, i) => {
            const status = row.status ?? row.assessment;
            const sCls = _assumptionStatusVariant(status);
            const _viewBody = [
              row.description ? `Description:\n${row.description}` : null,
              row.rationale ? `AI Analysis / Rationale:\n${row.rationale}` : null,
              row.stated_value != null && row.stated_value !== ''
                ? `Stated Value: ${row.stated_value}${row.unit ? ` ${row.unit}` : ''}`
                : null,
              row.benchmark_value != null
                ? `Benchmark Value: ${row.benchmark_value}${row.unit ? ` ${row.unit}` : ''}`
                : null,
              row.variance_pct != null
                ? `Variance: ${row.variance_pct.toFixed(2)}%`
                : null,
              row.assessment ? `Variance Band: ${row.assessment}` : null,
              row.flag ? `Flag: ${row.flag}` : null,
              row.status ? `AI Verdict: ${row.status}` : null,
              row.consultant ? `Consultant / Source: ${row.consultant}` : null,
              (row.source_document || row.source_page != null)
                ? `Citation: ${row.source_document ?? '(source unspecified)'}${row.source_page != null ? `, Page ${row.source_page}` : ''}`
                : null,
            ].filter(Boolean).join('\n\n');
            return (
              <tr key={`assumption-${startIdx + i}`}>
                <td className="td-name">
                  {_viewBody ? (
                    /* Iter4b §7 — inline cell renders the short
                       assumption title; the View modal opens with the
                       richer body (description + rationale + stated +
                       benchmark + variance + verdict + citation) via
                       the new modalBody prop. Without modalBody the
                       modal used to echo the inline children (short
                       title) and looked identical. */
                    <TruncatedCell
                      title={`Assumption — ${row.assumption_name}`}
                      text={row.assumption_name}
                      modalBody={_viewBody}
                      alwaysShowView
                    >
                      <span>{row.assumption_name}</span>
                    </TruncatedCell>
                  ) : (
                    <span>{row.assumption_name}</span>
                  )}
                </td>
                <td className="t-xs">
                  {/* Iter6 #1 — canonical SourceChip in place of the
                      plain "docname p.NN" text. */}
                  <SourceChip doc={row.source_document ?? null} page={row.source_page} />
                </td>
                <td>
                  <span
                    className={sCls}
                    style={{ cursor: 'help' }}
                    data-tip={row.rationale
                      ? cleanTooltip(`AI-derived: ${row.rationale}`)
                      : `AI-derived ${status ?? 'status'}: no additional rationale available.`}
                  >
                    {status ?? '—'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {showPager && (
        <div
          className="t-xs"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--s2) var(--s3)',
            borderTop: '1px solid var(--lines)',
            marginTop: 0,
          }}
        >
          <span className="t-muted">
            Showing {startIdx + 1}–{endIdx} of {rows.length}
          </span>
          <span style={{ display: 'inline-flex', gap: 'var(--s2)', alignItems: 'center' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={clampedPage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← Prev
            </button>
            <span className="t-muted">
              Page {clampedPage + 1} / {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={clampedPage >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            >
              Next →
            </button>
          </span>
        </div>
      )}
    </div>
  );
}

// Postgres JSONB does not preserve dict-key insertion order. Enforce a
// canonical column order on the frontend for any table whose order matters.
const DATA_TABLE_CANONICAL_ORDER: Record<string, string[]> = {
  risk_allocation: ['Risk Profile', 'Metric', 'Low', 'Medium', '', 'High'],
  geographical_allocation: ['Region'],
  // risk_profile rows come from union-merge across docs; show every
  // schema field in the canonical analyst-friendly order and append
  // Source last (synthesised from each row's source_document/source_page).
  risk_profile: ['risk', 'description', 'impact', 'likelihood', 'mitigation', 'Source'],
};

// Attributes whose table should render a per-row "Source" column built
// from row.source_document + row.source_page. Used by union-merge tables
// where the attribute-level source is "Multiple sources" and the per-row
// provenance is the useful signal.
const PER_ROW_SOURCE_ATTRS = new Set(['risk_profile']);

// geographical_allocation column names are dynamic (e.g. "Proforma
// Commitment as at 30.03.2023" contains a doc-specific date), so the
// static CANONICAL_ORDER map can't list them. Bucket by name pattern
// to enforce: Region -> Max/Limit -> Pre/Proforma -> Post/Status -> other.
function geoColumnBucket(col: string): number {
  const c = col.toLowerCase();
  if (c.includes('region')) return 0;
  if (c.includes('max') || c.includes('limit')) return 1;
  if (c.includes('proforma') || c.includes('pre')) return 2;
  if (c.includes('post') || c.includes('status')) return 3;
  return 4;
}

// Columns whose values are long narrative text — truncate in the table
// and show a "more" link that opens a modal with the full content.
const LONG_TEXT_COLUMNS: Record<string, Set<string>> = {
  risk_profile: new Set(['description', 'mitigation']),
};
const TRUNCATE_AT = 80;

function DataTable({ rows, attributeKey }: { rows: Record<string, unknown>[]; attributeKey?: string }) {
  const [modal, setModal] = useState<CellModal | null>(null);
  // Drop any rows that aren't plain objects — guards against LLM
  // occasionally returning rows as a list of strings ("Low", "Medium"),
  // which the renderer would otherwise iterate char-by-char as columns.
  const dictRows = (rows ?? []).filter(
    (r) => r != null && typeof r === 'object' && !Array.isArray(r),
  );
  if (!dictRows.length) return <p className="t-muted t-sm">No data.</p>;
  const showPerRowSource = attributeKey != null && PER_ROW_SOURCE_ATTRS.has(attributeKey);
  const longCols = attributeKey ? LONG_TEXT_COLUMNS[attributeKey] : undefined;

  // Collect the union of keys across ALL rows (not just rows[0]). Earlier
  // we used Object.keys(rows[0]) which silently dropped columns whenever
  // the first row happened to have a field null while a later row had it
  // populated — that's why Impact/Likelihood/Mitigation were missing.
  const keySet = new Set<string>();
  for (const row of dictRows) {
    for (const k of Object.keys(row)) {
      if (k === 'source_document' || k === 'source_page') continue;
      keySet.add(k);
    }
  }
  const rawColumns = Array.from(keySet);
  if (showPerRowSource) rawColumns.push('Source');

  const preferred = attributeKey ? DATA_TABLE_CANONICAL_ORDER[attributeKey] : undefined;
  let columns = rawColumns;
  if (attributeKey === 'geographical_allocation') {
    // Dynamic columns (date in column name) — bucket-sort by intent.
    columns = [...rawColumns].sort((a, b) => {
      const ba = geoColumnBucket(a), bb = geoColumnBucket(b);
      if (ba !== bb) return ba - bb;
      return rawColumns.indexOf(a) - rawColumns.indexOf(b);
    });
  } else if (preferred) {
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const col of preferred) {
      if (rawColumns.includes(col) && !seen.has(col)) {
        ordered.push(col);
        seen.add(col);
      }
    }
    for (const col of rawColumns) {
      if (!seen.has(col)) {
        ordered.push(col);
        seen.add(col);
      }
    }
    columns = ordered;
  }
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c.replace(/_/g, ' ')}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dictRows.map((row, i) => (
            <tr key={i}>
              {columns.map((c, ci) => {
                if (c === 'Source' && showPerRowSource) {
                  /* Sprint 5 client feedback 2026-07-12 — was hand-rolling the
                     chip, so a placeholder source ("—" from the BE) rendered
                     as a bordered chip containing a dash. SourceChip owns the
                     no-source case and returns a plain muted dash. */
                  const doc = row['source_document'];
                  const page = row['source_page'];
                  return (
                    <td key={c} style={{ whiteSpace: 'nowrap' }}>
                      <SourceChip
                        doc={doc != null ? String(doc) : null}
                        page={page != null ? String(page) : null}
                      />
                    </td>
                  );
                }
                const val = row[c];
                if (c.toLowerCase() === 'status') {
                  const s = String(val ?? '—');
                  const cls = s.includes('Within') || s === 'Pass' ? 'badge-pass'
                    : s.includes('Exceeds') || s === 'Fail' ? 'badge-fail'
                    : 'badge-neutral';
                  return <td key={c}><span className={`badge ${cls}`} style={{ fontSize: '0.7rem' }}>{s}</span></td>;
                }
                // Long-text columns (risk description / mitigation): truncate
                // in-table and offer a "View" button → opens modal.
                if (longCols?.has(c) && typeof val === 'string' && val.length > TRUNCATE_AT) {
                  const titleHint = String(row['risk'] ?? row['description'] ?? c);
                  return (
                    <td key={c}>
                      <div className="td-desc-wrap">
                        {/* Format Phase 3 — long-text cells route through
                            FormattedText so **bold** in an LLM-authored
                            description doesn't render literal asterisks
                            (see markdown_formatting_plan.md). */}
                        <span className="td-desc-text">
                          <FormattedText text={val} inline />
                        </span>
                        <button
                          type="button"
                          className="btn-view"
                          onClick={() => setModal({ title: `${c.charAt(0).toUpperCase()}${c.slice(1)} — ${titleHint}`, body: <FormattedText text={val} /> })}
                        >View</button>
                      </div>
                    </td>
                  );
                }
                // First column gets bold "name" treatment to match the mock.
                const tdClass = ci === 0 ? 'td-name' : '';
                return (
                  <td key={c} className={tdClass} style={{ fontFamily: typeof val === 'number' ? 'var(--font-mono)' : undefined }}>
                    {val == null || val === '' ? <span className="t-muted">—</span>
                      : typeof val === 'string' ? <FormattedText text={val} inline />
                      : String(val)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {modal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 'var(--s4)',
          }}
          onClick={() => setModal(null)}
        >
          <div
            className="card"
            style={{
              width: 'min(640px, 92vw)',
              maxHeight: 'min(80vh, 600px)',
              display: 'flex', flexDirection: 'column',
              background: 'var(--bg)', boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header">
              <h2 className="t-h3" style={{ margin: 0 }}>{modal.title}</h2>
              <button
                type="button"
                onClick={() => setModal(null)}
                aria-label="Close"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: 'var(--muted)' }}
              >×</button>
            </div>
            <div style={{ padding: 'var(--s4)', overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
              {modal.body}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ReportPage() {
  const { jobId: assessmentId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<AssessmentStatusResponse | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('section-01');
  // Sprint 5 client feedback 2026-06-25 — DD + SPA report pages no
  // longer carry any download UI; all download controls live on the
  // Risk Report. The `isDownloading` state, `downloadFromUrl`
  // helper, `handleDownload` (PDF), `handleLegalReviewDownload` (SPA),
  // and the prior `handleIcPaperDownload` are all retired. The
  // backing fields (analysis.legal_review_url, report_file_path,
  // ic_paper_url) remain on the payload for backwards compat with
  // prior assessments — only the UI bindings are removed.

  useEffect(() => {
    if (!assessmentId) return;
    Promise.all([
      getAssessmentStatus(assessmentId),
      /* Swallow analysis errors here so the page can still render with
         only the status data — the placeholder branch downstream picks
         the right wording based on status.status. We DO log the
         underlying error to the console so an analyst inspecting
         devtools can see whether it was 404 / 500 / network / etc. */
      getAssessmentAnalysis(assessmentId).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[ReportPage] analysis fetch failed for', assessmentId, err);
        return null;
      }),
    ])
      .then(([s, a]) => {
        setStatus(s);
        setAnalysis(a);
      })
      .catch((err) => {
        /* Sprint 5 fix — when the BE 404s with a structured
           {"error": "ASSESSMENT_NOT_FOUND"} body, the prior
           JSON.stringify branch surfaced raw "{"error":"ASSESSMENT_NOT_FOUND"}"
           to the analyst. That happens when a list entry points at an
           assessment row that no longer exists (deleted, migrated, or
           the list response is stale in browser cache). Translate the
           common codes into a friendly sentence + a suggestion to
           refresh the list. */
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const code = typeof detail === 'string'
          ? detail
          : (detail && typeof detail === 'object' && 'error' in detail
              ? String((detail as { error: unknown }).error)
              : null);
        const detailMsg = (detail && typeof detail === 'object' && 'message' in detail)
          ? String((detail as { message: unknown }).message)
          : null;
        let msg: string;
        if (status === 404 && code === 'ASSESSMENT_NOT_FOUND') {
          msg = (
            `Report not available — assessment '${assessmentId}' no longer exists. ` +
            `It may have been deleted, replaced by a re-run, or removed during a database migration. ` +
            `Refresh the Assessments list and try the most recent entry.`
          );
        } else if (status === 404 && code === 'ANALYSIS_NOT_FOUND') {
          msg = (
            `Analysis not yet available for ${assessmentId} — the assessment exists ` +
            `but the workflow hasn't produced a report row. If processing just finished, ` +
            `wait 10-30 seconds and reload.`
          );
        } else if (status === 404 && code === 'REPORT_NOT_AVAILABLE') {
          msg = `Report file missing for ${assessmentId}. The DOCX / PDF artefact may not have been rendered.`;
        } else if (detailMsg) {
          msg = detailMsg;
        } else if (typeof detail === 'string') {
          msg = detail;
        } else {
          msg = 'Failed to load report.';
        }
        // eslint-disable-next-line no-console
        console.error('[ReportPage] load failed', {
          assessmentId, status, code, detailMsg, raw: err?.response?.data,
        });
        setError(msg);
      })
      .finally(() => setIsLoading(false));
  }, [assessmentId]);

  // Scroll-spy: highlight the TOC entry whose section is currently in the
  // viewport. Uses IntersectionObserver with a rootMargin that treats the
  // top 15% / bottom 70% of the viewport as the "active band", so the
  // section roughly at the top of the visible area wins.
  //
  // Sprint 5 client feedback 2026-07-08 (UI Ticket — TOC scroll-spy
  // last-section fix) — a scroll listener also forces active = last
  // observed section when the user is within 40 px of document bottom.
  // Rationale: the last section is often too short to cross the
  // observer's active band (top 15% of viewport). Without this guard,
  // the TOC highlight stays on the previous section even after the
  // user has scrolled to the end. Generic — works for DD (up to §8),
  // SPA (up to §7), and any future section list because the "last
  // section" is derived from the id list at runtime.
  useEffect(() => {
    if (!analysis) return;
    // Section 06 'Other Characteristics' is deferred to a later sprint —
    // we omit it from the report entirely and renumber subsequent
    // sections so the TOC stays contiguous. The id list reflects what
    // CAN appear; some are conditionally pushed below.
    const ids = ['section-01','section-02','section-03','section-04','section-05','section-06','section-07','section-08'];
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const topmost = visible.reduce((best, cur) =>
          cur.boundingClientRect.top < best.boundingClientRect.top ? cur : best,
        );
        setActiveSection(topmost.target.id);
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 },
    );
    // Which section ids are actually mounted (some are conditional).
    const mountedIds = ids.filter((id) => document.getElementById(id));
    mountedIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    // Bottom-of-page guard — FIX 2. When scrolled within 40 px of the
    // document bottom, force active = last mounted section regardless
    // of intersection ratios so the TOC highlight lands on the last
    // TOC entry when the user actually reaches it.
    const onScroll = () => {
      if (!mountedIds.length) return;
      const scrolledToBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 40;
      if (scrolledToBottom) {
        setActiveSection(mountedIds[mountedIds.length - 1]);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      obs.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [analysis]);

  const sections: ReportSection[] = useMemo(() => {
    if (!analysis || !status) return [];

    // ── Sprint 4 US4 (T043) — SPA dispatch ────────────────────────────────
    // SPA workflow: render the 7-section Legal Review variant instead of
    // the 8-section DD report. Shared chrome (sticky TOC, topbar,
    // download button) stays in this page; only the section list swaps.
    // Per research.md R5 + frontend/design/spa-report.html.
    if (analysis.workflow_code === 'SPA') {
      return buildSpaSections(analysis, status);
    }

    const attrs = analysis.attribute_results ?? [];

    // ── Sprint 3 data-path swap (T022) ─────────────────────────────────────
    // The Section 01 metric tiles for Overall IRR and Equity Check prefer
    // the structured `attribute_groups` payload introduced in T021 when the
    // backend supplies it. Synthesised AttributeResult-shaped shims keep
    // the downstream rendering code below unchanged — it still reads
    // `irr.value` / `dealSize.benchmark_result.matched_column` / etc.
    // When the response is Sprint 2-only (`attribute_groups` is null), we
    // fall through to the original flat lookup so older assessments still
    // render correctly. Pure data-path swap, no UI change.
    const groups = analysis.attribute_groups ?? null;
    const dealSizeGroupEntry = groups?.deal_size?.oia_equity_usd_m ?? null;
    const irrGroupEntry = groups?.irr?.irr_pct ?? null;

    const dealSizeFlat = findAttr(attrs, 'deal_size');
    const irrFlat = findAttr(attrs, 'irr');

    /* Sprint 5 client feedback 2026-06-23 — when the numeric
       deal_size_oia_equity_usd_m group entry exists but its `value` is
       null (DD didn't extract a specific OIA commitment), fall back
       to the composite `deal_size` row's value (which usually carries
       the verbatim "Investor commitment: US$ 30M" text). formatEquityCheck
       below knows how to parse the labelled form into "$30M". Before
       this fallback, the card showed "Pending — —" even when the
       composite was clearly populated. */
    const groupHasUsableValue =
      dealSizeGroupEntry &&
      dealSizeGroupEntry.value !== null &&
      dealSizeGroupEntry.value !== undefined &&
      String(dealSizeGroupEntry.value).trim() !== '';
    const dealSize: AttributeResult | undefined = groupHasUsableValue
      ? {
          ...dealSizeFlat,  // fall back for any field the group doesn't carry
          attribute_key: 'deal_size_oia_equity_usd_m',
          value: dealSizeGroupEntry!.value,
          source_page: dealSizeGroupEntry!.source_page,
          source_document: dealSizeGroupEntry!.source_document ?? undefined,
          benchmark_result: dealSizeGroupEntry!.benchmark_result,
          value_type:
            dealSizeGroupEntry!.source_type === 'derived' ? 'Derived'
            : dealSizeGroupEntry!.source_type === 'missing' ? 'Not Found'
            : 'Stated',
        } as AttributeResult
      : dealSizeFlat;

    const irr: AttributeResult | undefined = irrGroupEntry
      ? {
          ...irrFlat,
          attribute_key: 'irr_pct',
          value: irrGroupEntry.value,
          source_page: irrGroupEntry.source_page,
          source_document: irrGroupEntry.source_document ?? undefined,
          benchmark_result: irrGroupEntry.benchmark_result,
          value_type:
            irrGroupEntry.source_type === 'derived' ? 'Derived'
            : irrGroupEntry.source_type === 'missing' ? 'Not Found'
            : 'Stated',
        } as AttributeResult
      : irrFlat;

    const holdingPeriod = findAttr(attrs, 'holding_period');
    const investmentType = findAttr(attrs, 'investment_type');
    const assetClass = findAttr(attrs, 'asset_class');
    const subSector = findAttr(attrs, 'sub_sector');
    const geography = findAttr(attrs, 'geography');
    const geoAlloc = findAttr(attrs, 'geographical_allocation');
    const riskAlloc = findAttr(attrs, 'risk_allocation');
    const riskProfile = findAttr(attrs, 'risk_profile');
    // Other Characteristics deferred to a future sprint — not displayed.

    const flagCount = analysis.flags?.length ?? 0;
    const overallBadge = analysis.overall_status ?? 'N/A';
    const overallCls = STATUS_BADGES[overallBadge] ?? 'badge-neutral';

    // Sprint 5 client feedback 2026-07-11 (Iter7 #5) — Overall Assessment
    // prose is composed from `${dealName} presents...` / `${dealName} analysis...`.
    // When deal_name is null (Project-workflow with no legacy free-text label),
    // the fallback was lowercase "this assessment" which produced sentences
    // starting with a lowercase "t". Capitalise so the sentence opens with
    // "This assessment presents..." per standard English grammar.
    const dealName = status.deal_name ?? 'This assessment';

    const built: ReportSection[] = [];

    // Section 01 — Executive Summary
    // Narrative is composed from the actual analysis output. We assemble
    // sentence fragments that describe IRR vs benchmark, holding period vs
    // benchmark, and high-severity flags — so the paragraph reads like
    // the design example instead of a generic "analysis completed" line.
    const irrPhrase = irr?.benchmark_result?.matched_column
      ? `IRR ${irr.value ?? ''} ${irr.benchmark_result.within_range === false ? 'falls outside' : 'falls within'} the ${irr.benchmark_result.matched_column} benchmark`
      : irr?.value ? `IRR ${irr.value} not yet benchmarked` : null;
    const hpPhrase = holdingPeriod?.benchmark_result?.matched_column
      ? `holding period ${holdingPeriod.value ?? ''} ${holdingPeriod.benchmark_result.within_range === false ? 'falls outside' : 'falls within'} the ${holdingPeriod.benchmark_result.matched_column} range`
      : holdingPeriod?.value ? `holding period ${holdingPeriod.value} not yet benchmarked` : null;
    const metricsLine = [irrPhrase, hpPhrase].filter(Boolean).join('; ');
    const flagLine = flagCount > 0
      ? ` ${flagCount === 1 ? 'One flag' : `${flagCount} flags`} require${flagCount === 1 ? 's' : ''} resolution prior to IC submission.`
      : '';
    const dealLine = investmentType?.value
      ? `${dealName} presents a ${investmentType.value.toLowerCase()} opportunity.`
      : `${dealName} analysis completed.`;
    const _rawNarrative = `${dealLine}${metricsLine ? ` ${metricsLine.charAt(0).toUpperCase()}${metricsLine.slice(1)}.` : ''}${flagLine}`;
    // Iter7 #5 — belt-and-braces first-char cap in case a deal_name arrives
    // lowercase or the fallback string ever regresses.
    const narrative = _rawNarrative
      ? _rawNarrative.charAt(0).toUpperCase() + _rawNarrative.slice(1)
      : _rawNarrative;
    // Map overallBadge to the result-* tint variant + risk-pill colour.
    const overviewVariant = overallBadge === 'Pass' ? 'result-pass'
      : overallBadge === 'Fail' ? 'result-fail'
      : 'result-watch';
    // Colour only the canonical "Regular" / "Value Add" / centre brackets
    // as Pass-green. Aggressive / Conservative / Opportunistic etc. match
    // a benchmark column but aren't a Pass per se — leave them neutral.
    // within_range === false (out of every bracket) stays red.
    const REGULAR_COLUMNS = new Set(['Regular', 'Value Add']);
    const irrMatched = irr?.benchmark_result?.matched_column ?? null;
    const irrPassClass = irr?.benchmark_result?.within_range === false ? 't-fail'
      : irrMatched && REGULAR_COLUMNS.has(irrMatched) ? 't-pass'
      : '';
    // Sprint 5 client feedback 2026-07-11 (Iter3 §1 Holding Period card)
    // — the pill on the §1 Holding Period card was showing every tier
    // the value satisfied (e.g. "Core / Core-Plus, Value Add,
    // Opportunistic"), same behaviour §4 had before Iter1. Apply the
    // same highest-only rule here so only the top-ranked band renders.
    // Rank: Core / Core-Plus < Value Add < Opportunistic.
    const _HP_TIER_RANK: Record<string, number> = {
      'Core / Core-Plus': 1,
      'Core / Debt': 1,
      'Conservative': 1,
      'Value Add': 2,
      'Aggressive': 2,
      'Opportunistic': 3,
    };
    const _hpMatchedRaw = holdingPeriod?.benchmark_result?.matched_column ?? null;
    const hpMatched = (() => {
      if (!_hpMatchedRaw) return null;
      const parts = String(_hpMatchedRaw).split(',').map((s) => s.trim()).filter(Boolean);
      const ranked = parts.filter((p) => _HP_TIER_RANK[p] != null);
      if (ranked.length === 0) return _hpMatchedRaw;
      return ranked.reduce((a, b) => (_HP_TIER_RANK[a] >= _HP_TIER_RANK[b] ? a : b));
    })();
    const hpPassClass = holdingPeriod?.benchmark_result?.within_range === false ? 't-fail'
      : hpMatched && REGULAR_COLUMNS.has(hpMatched) ? 't-pass'
      : '';
    built.push({
      id: 'section-01',
      num: '01',
      title: 'Executive Summary',
      body: (
        <>
          <div className={`${overviewVariant} result-overview`}>
            <div className="result-overview-header">
              <span className={`badge ${overallCls}`} style={{ flexShrink: 0 }}>● {overallBadge}</span>
              <strong>Overall Assessment</strong>
            </div>
            <p className="result-summary">{narrative}</p>
          </div>
          {(() => {
            const irrFill = tierFillSpec(irrMatched, irr?.benchmark_result?.within_range);
            const hpFill  = tierFillSpec(hpMatched,  holdingPeriod?.benchmark_result?.within_range);
            // Sprint 5 client feedback 2026-07-11 (Iter7 #7) — each metric
            // card gets a hover tooltip that explains WHY the tier was
            // assigned. Analysts previously had no way to distinguish an
            // OIA-policy verdict from an industry-benchmark verdict from
            // an internal fallback. Format:
            //   "{Metric} ({value}) has been classified as {tier} because
            //    it {comparator} the {range} benchmark defined by
            //    {benchmark source}."
            const _benchmarkSource = (_br: NonNullable<typeof irr>['benchmark_result'] | undefined): string => {
              // The backend BenchmarkResult schema doesn't yet carry a
              // provenance field — every DD tier benchmark currently
              // reads from the OIA Investment Policy source. Hard-code
              // that label until the BE schema gains a benchmark_source
              // string.
              void _br;
              return 'OIA Investment Policy — Final Approved 2024';
            };
            const _rangeFrom = (br: NonNullable<typeof irr>['benchmark_result'] | undefined): string | null => {
              const matchedCol = br?.matched_column;
              const rowValues = br?.row_values as Record<string, unknown> | undefined;
              if (!matchedCol || !rowValues) return null;
              const direct = rowValues[matchedCol];
              if (direct != null) return String(direct);
              if (typeof matchedCol === 'string' && matchedCol.includes(',')) {
                for (const piece of matchedCol.split(',').map((s) => s.trim())) {
                  const v = rowValues[piece];
                  if (v != null) return String(v);
                }
              }
              return null;
            };
            const _tierTooltip = (
              label: string,
              value: string | null | undefined,
              tier: string | null | undefined,
              br: NonNullable<typeof irr>['benchmark_result'] | undefined,
            ): string => {
              const v = value ?? '—';
              if (!tier) {
                return `${label} (${v}) is pending benchmark classification. Awaiting benchmark data before a tier can be assigned.`;
              }
              const range = _rangeFrom(br);
              const source = _benchmarkSource(br);
              const comparator = br?.within_range === false
                ? 'falls outside'
                : 'meets';
              const rangeClause = range
                ? ` because it ${comparator} the ${range} benchmark`
                : '';
              return `${label} (${v}) has been classified as ${tier}${rangeClause} defined by ${source}.`;
            };
            const _equityTooltip = (): string => {
              const v = formatEquityCheck(dealSize?.value);
              const totalRound = findAttr(attrs, 'deal_size_total_round_usd_m');
              const src = dealSize?.source_document
                ? (DOC_LABEL_SHORT[dealSize.source_document] ?? dealSize.source_document)
                : 'the Investment Memo';
              if (!dealSize?.value) {
                return `OIA Equity Check is pending — deal size not yet verified against a source document.`;
              }
              const round = totalRound?.value
                ? ` of the $${String(totalRound.value).replace(/\.0+$/, '')}M total round`
                : '';
              return `OIA Equity Check (${v}) has been verified against the deal size${round} as stated in ${src}.`;
            };
            const _irrTooltip = cleanTooltip(_tierTooltip('Overall IRR', formatIrr(irr?.value), irrMatched, irr?.benchmark_result));
            const _hpTooltip = cleanTooltip(_tierTooltip('Holding Period', holdingPeriod?.value != null ? String(holdingPeriod.value) : null, hpMatched, holdingPeriod?.benchmark_result));
            const _eqTooltip = cleanTooltip(_equityTooltip());
            return (
              <div className="metrics-grid">
                <div className="metric-card" data-tip={_irrTooltip} style={{ cursor: 'help' }}>
                  <div>
                    <div className="metric-card-head">
                      <p className="metric-label">Overall IRR</p>
                      {/* UI-SP2-11 — pill ALWAYS renders. When the
                          benchmark didn't classify, show a "Pending"
                          placeholder rather than leaving an empty slot. */}
                      {irrMatched ? (
                        <span className={`metric-pill ${tierPillClass(irrMatched, irr?.benchmark_result?.within_range)}`}>
                          {irrMatched}
                        </span>
                      ) : (
                        <span className="metric-pill tier-neutral">Pending</span>
                      )}
                    </div>
                    <div className="metric-value-row">
                      <div className={`metric-value ${irrPassClass}`}>{formatIrr(irr?.value)}</div>
                      <span className="metric-unit">projected</span>
                    </div>
                    {(() => {
                      // Caption ALWAYS renders. "Pass vs 18-22% benchmark"
                      // when classified, "Pending benchmark" otherwise.
                      // Sprint 5 (Second-Set Issue 3 carry-over): handle
                      // comma-joined multi-tier matched_column by trying
                      // each tier piece's range when the direct lookup
                      // misses, so the card never lingers on Pending
                      // after the engine has already matched.
                      const br = irr?.benchmark_result;
                      const matchedCol = br?.matched_column;
                      const rowValues = br?.row_values;
                      let range: string | null = null;
                      if (matchedCol && rowValues) {
                        const direct = (rowValues as Record<string, unknown>)[matchedCol];
                        if (direct != null) {
                          range = String(direct);
                        } else if (typeof matchedCol === 'string' && matchedCol.includes(',')) {
                          for (const piece of matchedCol.split(',').map((s) => s.trim())) {
                            const v = (rowValues as Record<string, unknown>)[piece];
                            if (v != null) { range = String(v); break; }
                          }
                        }
                      }
                      if (!range) {
                        return <p className="metric-caption">Pending benchmark</p>;
                      }
                      const verdict = br?.within_range === false ? 'Watch' : 'Pass';
                      return <p className="metric-caption">{verdict} vs {range} benchmark</p>;
                    })()}
                  </div>
                  <div className="metric-progress" aria-hidden="true">
                    <div className={`metric-progress-fill ${irrFill.cls}`} style={{ width: irrFill.width }} />
                  </div>
                </div>

                <div className="metric-card" data-tip={_hpTooltip} style={{ cursor: 'help' }}>
                  <div>
                    <div className="metric-card-head">
                      <p className="metric-label">Holding Period</p>
                      {hpMatched ? (
                        <span className={`metric-pill ${tierPillClass(hpMatched, holdingPeriod?.benchmark_result?.within_range)}`}>
                          {hpMatched}
                        </span>
                      ) : (
                        <span className="metric-pill tier-neutral">Pending</span>
                      )}
                    </div>
                    <div className="metric-value-row">
                      <div className={`metric-value ${hpPassClass}`}>{holdingPeriod?.value ?? '—'}</div>
                    </div>
                    {(() => {
                      /* Sprint 5 client feedback 2026-06-22 (Second-Set
                         Issue 3): Holding Period benchmarks against multiple
                         tier columns simultaneously (e.g. "Core / Core-Plus,
                         Value Add, Opportunistic"). The naive lookup
                         row_values[matched_column] fails for the
                         comma-joined key — no such column exists in
                         row_values, so range was null and the card
                         lingered on "Pending benchmark" even after the
                         benchmark engine had already matched. Now we try
                         the joined key first, then split by comma and use
                         the FIRST matching tier's range; "Pending" only
                         renders when no tier in the match list has a range. */
                      const br = holdingPeriod?.benchmark_result;
                      const matchedCol = br?.matched_column;
                      const rowValues = br?.row_values;
                      let range: string | null = null;
                      if (matchedCol && rowValues) {
                        const direct = (rowValues as Record<string, unknown>)[matchedCol];
                        if (direct != null) {
                          range = String(direct);
                        } else if (typeof matchedCol === 'string' && matchedCol.includes(',')) {
                          for (const piece of matchedCol.split(',').map((s) => s.trim())) {
                            const v = (rowValues as Record<string, unknown>)[piece];
                            if (v != null) { range = String(v); break; }
                          }
                        }
                      }
                      if (!range) {
                        return <p className="metric-caption">Pending benchmark</p>;
                      }
                      const verdict = br?.within_range === false ? 'Watch' : 'Pass';
                      return <p className="metric-caption">{verdict} vs {range} benchmark</p>;
                    })()}
                  </div>
                  <div className="metric-progress" aria-hidden="true">
                    <div className={`metric-progress-fill ${hpFill.cls}`} style={{ width: hpFill.width }} />
                  </div>
                </div>

                <div className="metric-card" data-tip={_eqTooltip} style={{ cursor: 'help' }}>
                  <div>
                    <div className="metric-card-head">
                      <p className="metric-label">OIA Equity Check</p>
                      {dealSize?.value ? (
                        <span className="metric-pill tier-verified">Verified</span>
                      ) : (
                        <span className="metric-pill tier-neutral">Pending</span>
                      )}
                    </div>
                    <div className="metric-value-row">
                      <div className="metric-value">{formatEquityCheck(dealSize?.value)}</div>
                    </div>
                    {(() => {
                      const totalRound = findAttr(attrs, 'deal_size_total_round_usd_m');
                      const src = dealSize?.source_document;
                      const parts: string[] = [];
                      if (totalRound?.value) {
                        /* Sprint 5 client feedback 2026-06-22 — strip
                           trailing zeros from the total-round value so
                           "350.0000" renders as "$350M total round"
                           instead of "$350.0000M total round". */
                        const tr = Number(String(totalRound.value).replace(/,/g, ''));
                        const trDisplay = !Number.isNaN(tr) && Number.isFinite(tr)
                          ? `$${tr === Math.trunc(tr) ? Math.trunc(tr) : tr.toFixed(2).replace(/\.?0+$/, '')}M`
                          : `$${totalRound.value}M`;
                        parts.push(`${trDisplay} total round`);
                      }
                      if (src) parts.push(DOC_LABEL_SHORT[src] ?? src);
                      if (parts.length === 0) {
                        return <p className="metric-caption">Pending source</p>;
                      }
                      return <p className="metric-caption">{parts.join(' · ')}</p>;
                    })()}
                  </div>
                  {/* Bottom-anchored progress bar for visual parity with
                      the other two cards. Equity check is binary
                      (verified or not) so the bar fills 100% / 0%. */}
                  <div className="metric-progress" aria-hidden="true">
                    <div
                      className={`metric-progress-fill ${dealSize?.value ? '' : 'fill-neutral'}`}
                      style={{ width: dealSize?.value ? '100%' : '0%' }}
                    />
                  </div>
                </div>
              </div>
            );
          })()}
        </>
      ),
    });

    // Section 02 — Geographical & Risk Allocation
    if (geoAlloc || riskAlloc) {
      built.push({
        id: 'section-02',
        num: '02',
        title: 'Geographical & Risk Allocation',
        body: (
          <>
            {geoAlloc && (
              <>
                <p className="subsec-title">Geographical Allocation</p>
                {geoAlloc.value_type === 'Not Found' ? (
                  <p className="t-muted t-sm">Not extracted.</p>
                ) : (
                  <>
                    <DataTable rows={geoAlloc.table_rows ?? []} attributeKey="geographical_allocation" />
                    <SourceLine attr={geoAlloc} />
                  </>
                )}
              </>
            )}
            {riskAlloc && (
              // Sprint 5 client feedback 2026-07-10 (DD Step2 §2) —
              // space above the Risk Allocation heading so it doesn't
              // hug the Geographic Allocation source line.
              <div style={{ marginTop: 'var(--s4)' }}>
                <p className="subsec-title">Risk Allocation</p>
                {riskAlloc.value_type === 'Not Found' ? (
                  <p className="t-muted t-sm">Not extracted.</p>
                ) : (
                  <>
                    <DataTable rows={riskAlloc.table_rows ?? []} attributeKey="risk_allocation" />
                    <SourceLine attr={riskAlloc} />
                  </>
                )}
              </div>
            )}
          </>
        ),
      });
    }

    // Section 03 — Investment Classification (4-field card + dynamic benchmark)
    const confirmedType = analysis.investment_type?.label ?? investmentType?.value ?? null;
    // Sprint 5 client feedback 2026-07-06 (2nd pass) — for the geography-
    // gated "Direct Investment" label, drop the TSG- prefix and use
    // proper spacing ("DIRECT INVESTMENT"). Other types keep the legacy
    // TSG-<KEY> format so historical branding stays intact.
    const benchmarkCode = analysis.investment_type?.key
      ? (analysis.investment_type.key === 'DirectInvestment'
          ? 'DIRECT INVESTMENT'
          : `${BRAND_CODE}-${analysis.investment_type.key.toUpperCase()}`)
      : null;
    // SELECTED BENCHMARK TABLE — render the benchmark thresholds for the
    // confirmed investment type so the analyst sees the targets the deal
    // is being measured against (UI-32, team feedback). The API returns
    // the full YAML benchmark_tables.<investment_type> block. Row order
    // mirrors the reference table screenshot — Risk Profile first, then
    // numerics, characteristics last.
    const renderBenchmarkRow = (
      label: string,
      key: string,
      cols: string[],
      rows: Record<string, Record<string, unknown>>,
    ) => {
      const row = rows[key];
      if (!row) return null;
      // Other Characteristics holds arrays per column (bullet lists);
      // everything else is a scalar string.
      const isList = Object.values(row).some((v) => Array.isArray(v));
      // Sprint 5 client feedback 2026-07-10 (DD.docx §3.6) — Other
      // Characteristics rendered as a bulleted list with visible
      // disc markers, and each item's first character upper-cased so
      // an inconsistently-cased YAML entry ("yield play") still reads
      // sentence-cased in the report.
      const _sentenceCase = (s: string): string =>
        !s ? s : s.charAt(0).toUpperCase() + s.slice(1);
      return (
        <tr key={key}>
          <td className="td-name" style={{ verticalAlign: 'top' }}>{label}</td>
          {cols.map((c) => {
            const v = row[c];
            if (Array.isArray(v)) {
              return (
                <td key={c} className="t-sm" style={{ verticalAlign: 'top' }}>
                  <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5, listStyleType: 'disc' }}>
                    {v.map((item, idx) => (
                      <li key={idx}>{_sentenceCase(String(item))}</li>
                    ))}
                  </ul>
                </td>
              );
            }
            return (
              <td key={c} className="t-sm" style={{ verticalAlign: isList ? 'top' : 'middle' }}>
                {v != null && v !== '' ? String(v) : <span className="t-muted">—</span>}
              </td>
            );
          })}
        </tr>
      );
    };
    const bt = analysis.benchmark_table as
      | { classification_columns?: string[]; rows?: Record<string, Record<string, unknown>> }
      | null
      | undefined;
    const benchmarkTableBlock = bt && bt.rows && bt.classification_columns
      ? (() => {
          const cols = bt.classification_columns ?? [];
          const rows = bt.rows ?? {};
          return (
            <div style={{ marginTop: 'var(--s4)' }}>
              {/* Sprint 5 client feedback 2026-07-10 (DD Step2 §3) —
                  header tooltip surfaces the source policy document
                  the benchmark thresholds come from. */}
              <p
                className="t-label"
                style={{ marginBottom: 'var(--s2)' }}
                data-tip="Benchmark thresholds sourced from OIA Investment Policy Final-Approved 2024"
              >
                SELECTED BENCHMARK TABLE <span style={{ opacity: 0.5, cursor: 'help' }}>ⓘ</span>
              </p>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Typical Deal Characteristics</th>
                      {cols.map((c) => <th key={c}>{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {renderBenchmarkRow('Risk Profile', 'risk_profile', cols, rows)}
                    {renderBenchmarkRow('Holding Period', 'holding_period', cols, rows)}
                    {renderBenchmarkRow('Typical Deal Size', 'typical_deal_size', cols, rows)}
                    {renderBenchmarkRow('Target IRRs', 'target_irr', cols, rows)}
                    {renderBenchmarkRow('EV/EBITDA Exit Multiple', 'ev_ebitda_exit_multiple', cols, rows)}
                    {renderBenchmarkRow('Other Characteristics', 'other_characteristics', cols, rows)}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()
      : null;
    if (investmentType || analysis.investment_type || assetClass || subSector || geography) {
      built.push({
        id: 'section-03',
        num: '03',
        title: 'Investment Classification',
        body: (
          <>
            <p className="subsec-title">Investment Type</p>
            <div className="classify-grid">
              <div className="card card-sm">
                {/* Sprint 5 client feedback 2026-07-11 (Iter3 §3.1) — the
                    "CLASSIFICATION" card heading was reading in muted
                    grey which blends into the background. The client
                    asked ONLY this heading to switch to the primary
                    text colour so the card is immediately identifiable
                    on scan. Other labels/values in the card must
                    remain untouched — hence the local override
                    instead of touching global .t-label. */}
                <p
                  className="t-label t-label-mb3"
                  style={{ color: 'var(--text)' }}
                >
                  Classification
                </p>
                <div className="classify-list">
                  <div className="classify-row">
                    <span className="t-muted">Investment Type</span>
                    <span
                      className="classify-val"
                      data-tip={investmentType ? `Source: ${investmentType.source_document ??investmentType.source_document ?? 'Unknown'}${investmentType.source_page != null ? ` p.${investmentType.source_page}` : ''}` : undefined}
                    >
                      {confirmedType ?? <span className="t-muted">Not confirmed</span>}
                      {investmentType?.source_document && (
                        <span className="t-xs t-muted" style={{ marginLeft: 6 }}>
                          · {investmentType.source_document ??investmentType.source_document}{investmentType.source_page != null ? ` p.${investmentType.source_page}` : ''}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="classify-row">
                    <span className="t-muted">Asset Class</span>
                    <span
                      className="classify-val"
                      data-tip={assetClass ? `Source: ${assetClass.source_document ??assetClass.source_document ?? 'Unknown'}${assetClass.source_page != null ? ` p.${assetClass.source_page}` : ''}` : undefined}
                    >
                      {assetClass?.value ?? <span className="t-muted">—</span>}
                      {assetClass?.source_document && (
                        <span className="t-xs t-muted" style={{ marginLeft: 6 }}>
                          · {assetClass.source_document ??assetClass.source_document}{assetClass.source_page != null ? ` p.${assetClass.source_page}` : ''}
                        </span>
                      )}
                    </span>
                  </div>
                  {/* Sprint 5 client feedback 2026-07-08 — DD §3 shows
                      Sector ONLY (parsed parent from the sub_sector
                      attribute); Sub-sector is left to Risk §4 which
                      renders both halves. The sub_sector extractor
                      returns the verbatim memo phrase
                      ("Energy Transition (Lithium-Ion Batteries)" /
                       "Green Hydrogen - Electrolyser Industry") and
                      the shared parser
                      (frontend/src/utils/subsectorParser.ts) splits
                      it on parenthetical / em-dash / en-dash / plain-
                      hyphen / colon / slash / comma. If the phrase
                      has no separator (e.g. "Renewables"), the whole
                      phrase becomes the Sector. If extraction
                      returned a failure marker (e.g. "Extraction
                      error"), that flows through verbatim so QA can
                      see the failure. Source citation still points
                      at the sub_sector attribute — same origin. */}
                  {(() => {
                    const parsed = parseSectorSubsector(
                      subSector?.value ? String(subSector.value) : null,
                    );
                    return (
                  <div className="classify-row">
                    <span className="t-muted">Sector</span>
                    <span
                      className="classify-val"
                      data-tip={subSector ? `Source: ${subSector.source_document ?? 'Unknown'}${subSector.source_page != null ? ` p.${subSector.source_page}` : ''}` : undefined}
                    >
                      {parsed.sector ?? <span className="t-muted">—</span>}
                      {subSector?.source_document && (
                        <span className="t-xs t-muted" style={{ marginLeft: 6 }}>
                          · {subSector.source_document}{subSector.source_page != null ? ` p.${subSector.source_page}` : ''}
                        </span>
                      )}
                    </span>
                  </div>
                    );
                  })()}
                  <div className="classify-row">
                    <span className="t-muted">Geography</span>
                    <span
                      className="classify-val"
                      data-tip={geography ? `Source: ${geography.source_document ??geography.source_document ?? 'Unknown'}${geography.source_page != null ? ` p.${geography.source_page}` : ''}` : undefined}
                    >
                      {geography?.value ?? <span className="t-muted">—</span>}
                      {geography?.source_document && (
                        <span className="t-xs t-muted" style={{ marginLeft: 6 }}>
                          · {geography.source_document ??geography.source_document}{geography.source_page != null ? ` p.${geography.source_page}` : ''}
                        </span>
                      )}
                    </span>
                  </div>
                  {/* Sprint 5 client feedback 2026-07-10 (DD.docx §3.2) —
                      "Confirmation source" row removed at client's
                      request; the investment_type source already
                      surfaces on the Investment Type row's citation
                      suffix. */}
                </div>
              </div>
              <div className="card card-sm benchmark-card">
                <p className="t-label t-label-mb2">Benchmark Applied</p>
                {benchmarkCode ? (
                  <>
                    <div className="benchmark-id">{benchmarkCode}</div>
                    {/* Sprint 5 client feedback 2026-07-10 (DD.docx §3.3) —
                        replace the ad-hoc "[assetClass] · [geography] ·
                        [year]" descriptor with the source policy
                        document name. Hardcoded per client direction
                        (2026-07-10 answer); later can be config-driven
                        via BENCHMARK_POLICY_DOC_NAME setting. */}
                    <p className="t-xs t-muted" style={{ margin: 0 }}>
                      OIA Investment Policy Final-Approved 2024
                    </p>
                  </>
                ) : (
                  <div className="t-sm t-muted">Pending Investment Type</div>
                )}
              </div>
            </div>

            {investmentType && (
              <div style={{ marginTop: 'var(--s3)' }}>
                <SourceLine attr={investmentType} />
              </div>
            )}
            {benchmarkTableBlock}
          </>
        ),
      });
    }

    // Section 04 — Financial Parameters (Sprint 1 simplified — 4 cols:
    // Metric / Value / Source / Source Type. Benchmark Range +
    // Classification columns removed per Sprint 1 scope.)
    // Section 04 — Financial Parameters & Benchmark Classification
    // (team feedback #2). Six columns: Metric / Value / Source / Source
    // Type / Benchmark Range / Classification. Each row pulls the
    // benchmark range from attr.benchmark_result.row_values[matched]
    // and renders the matched profile as a coloured pill. Rows whose
    // attribute has no benchmark configured render "—" + "Review".
    const xirr = findAttr(attrs, 'xirr_pct');
    const revenueUsdM = findAttr(attrs, 'revenue_usd_m');
    const ebitdaUsdM = findAttr(attrs, 'ebitda_usd_m');
    const entryEvUsdM = findAttr(attrs, 'entry_ev_usd_m');
    const evEbitdaMultiple = findAttr(attrs, 'ev_ebitda_multiple');
    if (dealSize || irr || holdingPeriod || xirr || revenueUsdM || ebitdaUsdM || entryEvUsdM || evEbitdaMultiple) {
      // Sprint 5 client feedback 2026-06-30 — Revenue / EBITDA /
      // Multiple / Enterprise Value labels carry a "Projected"
      // prefix ONLY when the surfaced value is a future-year figure
      // (e.g. FY2028 forecast from the IC memo's valuation table).
      // When the extractor surfaced a current-year actual the label
      // stays plain. Year inference walks the attribute's
      // derivation_detail, then falls back to scanning quoted_text /
      // raw_value for a 4-digit year and picking the latest.
      const _currentYear = new Date().getFullYear();
      const _yearOf = (a: AttributeResult | undefined | null): number | null => {
        if (!a) return null;
        const rec = a as unknown as Record<string, unknown>;
        const dd = rec['derivation_detail'];
        if (dd && typeof dd === 'object') {
          const ddRec = dd as Record<string, unknown>;
          for (const k of ['target_year', 'projection_year', 'year', 'fiscal_year', 'period', 'as_of_year']) {
            const v = ddRec[k];
            if (v != null) {
              const m = String(v).match(/\b(20\d{2})\b/);
              if (m) return parseInt(m[1], 10);
            }
          }
        }
        const haystack = [
          rec['quoted_text'],
          rec['source_text'],
          a.value,
        ].filter((v) => v != null).map(String).join(' ');
        const matches = haystack.match(/\b(20\d{2})\b/g);
        if (matches && matches.length > 0) {
          return Math.max(...matches.map((y) => parseInt(y, 10)));
        }
        return null;
      };
      const _projectedLabel = (base: string, attr: AttributeResult | undefined): string => {
        const y = _yearOf(attr);
        if (y != null && y > _currentYear) {
          return `Projected ${base} (FY${y})`;
        }
        return base;
      };

      const financialMetrics: { label: string; attr: AttributeResult | undefined }[] = [
        { label: 'Projected IRR', attr: irr },
        { label: 'XIRR', attr: xirr },
        { label: 'Holding Period', attr: holdingPeriod },
        { label: 'Deal Size (OIA Equity Check)', attr: dealSize },
        { label: _projectedLabel('Revenue', revenueUsdM), attr: revenueUsdM },
        { label: _projectedLabel('EBITDA', ebitdaUsdM), attr: ebitdaUsdM },
        { label: _projectedLabel('EV/EBITDA Multiple', evEbitdaMultiple), attr: evEbitdaMultiple },
        // Sprint 5 client feedback 2026-06-24 — relabelled "Entry
        // EV" to "Enterprise Value (EBITDA × multiple)" to match
        // the DOCX export (dd_template.py:558 + DD.yaml calc_name).
        // The label reads truthfully whichever year's EBITDA the
        // extractor surfaced (current → Entry EV; FY+N projected →
        // Exit EV at that horizon).
        { label: _projectedLabel('Enterprise Value (EBITDA × Multiple)', entryEvUsdM), attr: entryEvUsdM },
      ].filter((m) => m.attr);

      const benchmarkRangeText = (attr: AttributeResult): string | null => {
        const br = attr.benchmark_result;
        if (!br || !br.row_values) return null;
        const matched = br.matched_column;
        if (matched) {
          // Could be a single column or comma-separated multi-match.
          const cols = matched.split(',').map((s) => s.trim()).filter(Boolean);
          // Dedup the range strings — if multiple columns share the same
          // value (e.g. Direct Investment Value Add + Opportunistic both
          // 3-7 Years), show it once.
          const ranges = Array.from(new Set(
            cols.map((c) => br.row_values?.[c]).filter((v) => v != null).map(String)
          ));
          if (ranges.length > 0) return ranges.join(', ');
        }
        // No specific match — show the full range across columns so the
        // analyst can still see what was checked.
        const parts = (br.classification_columns ?? []).map((c) => {
          const v = br.row_values?.[c];
          return v != null ? `${c}: ${v}` : null;
        }).filter(Boolean);
        return parts.length > 0 ? parts.join(' · ') : null;
      };

      // Sprint 5 client feedback 2026-07-10 (Iter1 §4) — when a metric
      // matches multiple benchmark bands, show only the HIGHEST one
      // (client rank: Core / Core-Plus < Value Add < Opportunistic).
      // Keeps the benchmark labels (not Low/Med/High) per Step2
      // counter-feedback but collapses multi-match rows to a single
      // chip so §4 stays scannable.
      const _BENCHMARK_META: Record<string, { rank: number; color: string }> = {
        'Core / Core-Plus':  { rank: 1, color: 'risk-low' },
        'Core / Debt':       { rank: 1, color: 'risk-low' },
        'Conservative':      { rank: 1, color: 'risk-low' },
        'Value Add':         { rank: 2, color: 'risk-medium' },
        'Aggressive':        { rank: 2, color: 'risk-medium' },
        'Opportunistic':     { rank: 3, color: 'risk-high' },
      };
      const classificationBadge = (attr: AttributeResult) => {
        const matched = attr.benchmark_result?.matched_column;
        if (!matched) {
          const hasValue =
            attr.value !== null && attr.value !== undefined && String(attr.value).trim() !== '';
          if (hasValue) {
            return <span className="badge badge-neutral chip-nodot" style={{ fontSize: '0.7rem' }}>Benchmark N/A</span>;
          }
          return <span className="badge badge-watch chip-nodot" style={{ fontSize: '0.7rem' }}>Review Required</span>;
        }
        const parts = matched.split(',').map((s) => s.trim()).filter(Boolean);
        const ranked = parts
          .map((p) => ({ label: p, meta: _BENCHMARK_META[p] }))
          .filter((x) => x.meta);
        if (ranked.length === 0) {
          return <span className="badge badge-neutral chip-nodot" style={{ fontSize: '0.7rem' }}>—</span>;
        }
        const highest = ranked.reduce((a, b) => (a.meta.rank >= b.meta.rank ? a : b));
        return <span className={`${highest.meta.color} chip-nodot`}>{highest.label}</span>;
      };

      // Sprint 5 client feedback 2026-07-10 (DD.docx §4.1 + Iter2 §4)
      // — append the metric's semantic unit so bare values like "30"
      // or "6.27" render with the unit an analyst expects. Iter2:
      // Deal Size wasn't picking up "$X Million" — hedged by also
      // keying on the row label so the formatter fires even if the
      // extractor emits an unexpected attribute_key. Broadened the
      // "M for million" detection to match "$30M", "USD 30M", and
      // "30 mn" variants so we don't double-append a unit.
      const _formatMetricValue = (
        attributeKey: string,
        rawValue: unknown,
        label?: string,
      ): string => {
        if (rawValue === null || rawValue === undefined) return '';
        const s = String(rawValue).trim();
        if (!s || s === '—' || s === 'Not found in documents') return s;
        const hasPct = /%$/.test(s);
        const hasDollar = /^\$/.test(s) || /^usd\b/i.test(s);
        const hasX = /x$/i.test(s);
        const hasYears = /years?$/i.test(s);
        const hasMillion = /(million|\bmn\b|\bm$|\$\s*\d+\s*m)/i.test(s);
        const lbl = (label ?? '').toLowerCase();
        const isMoneyMillion =
          attributeKey === 'deal_size'
          || attributeKey === 'revenue_usd_m'
          || attributeKey === 'ebitda_usd_m'
          || attributeKey === 'entry_ev_usd_m'
          || lbl.includes('deal size')
          || lbl.includes('revenue')
          || lbl.includes('ebitda')
          || lbl.includes('enterprise value');
        const isPercent = attributeKey === 'irr_pct' || attributeKey === 'xirr_pct'
          || lbl.includes('irr') || lbl.includes('xirr');
        const isYears = attributeKey === 'holding_period' || lbl.includes('holding period');
        const isMultiple = attributeKey === 'ev_ebitda_multiple'
          || lbl.includes('ev/ebitda') || lbl.includes('multiple');
        /* Sprint 5 client feedback 2026-07-11 (Iter7 #1) — isMultiple
           must be checked BEFORE isMoneyMillion because
           "EV / EBITDA Multiple" as a label contains the substring
           "ebitda", which was tripping the money-million branch and
           rendering the multiple as "$6.27 Million" instead of
           "6.27x". Reordering the guards fixes the mis-classification
           without touching the isMoneyMillion set (which stays
           label-broad so ambiguous rows like plain "EBITDA" still
           render with the correct USD Million unit). */
        if (isMultiple) return hasX ? s : `${s}x`;
        if (isPercent) return hasPct ? s : `${s}%`;
        if (isYears) return hasYears ? s : `${s} Years`;
        if (isMoneyMillion) {
          if (hasDollar && hasMillion) return s;
          if (hasDollar) return `${s} Million`;
          if (hasMillion) return s.startsWith('$') ? s : `$${s}`;
          return `$${s} Million`;
        }
        return s;
      };

      built.push({
        id: 'section-04',
        num: '04',
        title: 'Financial Parameters & Benchmark Classification',
        body: (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Value</th>
                  <th>Source</th>
                  <th>Source Type</th>
                  {/* Sprint 5 client feedback 2026-07-10 (DD.docx §4.2) —
                      Benchmark Range header tooltip names the source
                      policy document so the analyst knows where the
                      threshold values come from. */}
                  <th data-tip="Benchmark thresholds sourced from OIA Investment Policy Final-Approved 2024">
                    Benchmark Range
                  </th>
                  {/* Sprint 5 client feedback 2026-07-11 (Iter4b §4) —
                      Classification header carries a formatted tooltip
                      explaining that the labels shown are the AI's
                      benchmark comparison result against OIA policy.
                      pre-line so the three-paragraph explanation
                      renders scannably in the native browser tooltip. */}
                  <th data-tip={_SECTION_04_CLASSIFICATION_TOOLTIP} style={{ whiteSpace: 'pre-line' }}>
                    Classification <span style={{ opacity: 0.5, cursor: 'help' }}>ⓘ</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {financialMetrics.map(({ label, attr }) => {
                  if (!attr) return null;
                  // Sprint 5 client feedback 2026-07-07 (BE Issue #1) —
                  // Not Found + User Input used to fall through to the
                  // base "src-type" class, producing class="src-type
                  // src-type" (no variant CSS matched). Full mapping
                  // below; unknown values land on "src-unknown" so we
                  // never re-emit the base class as the variant slot.
                  const sourceTypeClass = _sourceTypeVariant(attr.value_type);
                  const sourceTypeLabel = attr.value_type === 'Stated' ? 'Extracted' : (attr.value_type ?? '—');
                  // Short-label mapping now lives inside SourceChip.
                  const range = benchmarkRangeText(attr);
                  const formattedValue = _formatMetricValue(attr.attribute_key, attr.value, label);
                  return (
                    <tr key={attr.attribute_key}>
                      <td className="td-name">{label}</td>
                      <td><strong>{formattedValue || <span className="t-muted">—</span>}</strong></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {/* Canonical chip — owns the no-source case, so a
                            placeholder "—" can't become a chip label. */}
                        <SourceChip
                          doc={attr.source_document ?? null}
                          page={attr.source_page ?? null}
                        />
                      </td>
                      <td>
                        <span className={`src-type ${sourceTypeClass}`}>{sourceTypeLabel}</span>
                      </td>
                      <td className="t-sm">{range ?? <span className="t-muted">—</span>}</td>
                      <td>{classificationBadge(attr)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ),
      });
    }

    // Section 05 — Risk Profile
    if (riskProfile) {
      built.push({
        id: 'section-05',
        num: '05',
        title: 'Risk Profile',
        body: riskProfile.value_type === 'Not Found' ? (
          <p className="t-muted t-sm">Not extracted from any document.</p>
        ) : (
          <>
            {/* Sprint 5 client feedback 2026-06-30 (New Issues D5) —
                Overall Risk Assessment panel: composite verdict
                derived from row severities + Benchmark Profile from
                §3 Investment Classification. Mirrors the Risk Report's
                §5 KPI strip so DD and Risk surfaces tell the same
                aggregate story. */}
            <OverallRiskAssessmentPanel
              rows={(riskProfile.table_rows ?? []) as Record<string, unknown>[]}
              invTypeKey={analysis.investment_type?.key ?? null}
              summary={(
                (analysis as unknown as Record<string, unknown>)['risk_profile'] as
                  | { summary?: { overall_rating?: string | null; benchmark_profile?: string | null } }
                  | undefined
              )?.summary ?? null}
            />
            <RiskProfileTable rows={riskProfile.table_rows ?? []} />
          </>
        ),
      });
    }

    // Other Characteristics (Sprint 3 US3; FR-REPORT-022)
    // characteristics_signal.py runs Qdrant searches per profile column
    // and classifies each keyword as Supported / Partial / Not Supported.
    // Three side-by-side (or stacked at narrow widths) tables, one per
    // column. Column slugs come from the YAML investment-type config so
    // Real Estate Funds correctly shows "Core / Debt", not the C/R/A names.
    // Placement: BEFORE Key Assumptions per analyst team request — the
    // characteristics frame how the deal sits within its benchmark, so
    // analysts review them before drilling into assumption variance.
    const signalsByColumn = groups?.characteristics_signal_matching ?? null;
    if (signalsByColumn && Object.keys(signalsByColumn).length > 0) {
      const columnLabel = (slug: string): string => {
        if (slug === 'core_debt') return 'Core / Debt';
        if (slug === 'core_core_plus') return 'Core / Core-Plus';
        return slug
          .split('_')
          .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
          .join(' ');
      };

      // Sprint 5 client feedback 2026-07-10 (DD Step2 §6.4) —
      // Characteristic column values sentence-cased so mixed-casing
      // YAML entries (e.g. "yield play") always render capitalised.
      /* Sprint 5 client feedback 2026-07-11 (Iter7 #2) — normalise
         every characteristic name to Title Case so the column reads
         consistently: "Value Add", "Income Producing", "Strategic
         Partner", "Growth" (not "Value add" / "Income producing").
         Small connector words (of, and, in, on, at, by, for, to,
         the, with, plus hyphenated fragments like "and") stay
         lowercase unless they open the string. Hyphen-separated
         compounds capitalise both halves ("Mixed-Use" stays
         "Mixed-Use"). */
      const _titleCaseChar = (s: string): string => {
        if (!s) return s;
        const _lowercase = new Set([
          'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'if', 'in',
          'nor', 'of', 'on', 'or', 'per', 'the', 'to', 'up', 'via', 'vs',
          'with',
        ]);
        const parts = s.trim().split(/(\s+)/);
        return parts
          .map((part, idx) => {
            if (/^\s+$/.test(part)) return part;
            const first = idx === 0;
            return part
              .split(/(-)/)
              .map((seg, si) => {
                if (seg === '-') return seg;
                const low = seg.toLowerCase();
                if (!first && si === 0 && _lowercase.has(low)) return low;
                return low.charAt(0).toUpperCase() + low.slice(1);
              })
              .join('');
          })
          .join('');
      };
      /* Legacy alias — some existing calls used _sentenceCaseChar
         which only uppercased the first letter. Redirect to the new
         Title Case helper so every call site gets the same
         normalisation. */
      const _sentenceCaseChar = _titleCaseChar;
      // Sprint 5 client feedback 2026-07-10 (DD Step2 §6.1) — Status
      // column tooltip explains the AI status derivation.
      /* Sprint 5 client feedback 2026-07-11 (Iter4 D5) — formatted
         Status tooltip. Line-breaks let the browser render this as
         a scannable definition list instead of one long wrapped
         sentence. Applied via whiteSpace:pre-line on the <th>. */
      // Iter7 #11 — bullet-prefixed for browsers that collapse \n in
      // native title attributes; matches the §7 Assumptions tooltip
      // format for consistency across DD report tooltips.
      const _STATUS_TOOLTIP =
        'Status — AI-determined verdict on each characteristic.\n\n'
        + '• Matched — the reviewed evidence directly supports the requirement.\n'
        + '• Partial — the evidence supports the requirement with caveats.\n'
        + '• Not Supported — the evidence contradicts or fails to satisfy the requirement.\n'
        + '• Not Available — no relevant information was found in the reviewed documents.';
      const renderColumnTable = (slug: string, rows: typeof signalsByColumn[string]) => (
        // Sprint 5 client feedback 2026-07-10 (DD Step2 §6.3) — reduce
        // vertical spacing between benchmark category tables so
        // Opportunistic no longer opens with a large gap below Value
        // Add. Only a tight top margin from the second table onward.
        <div key={slug} style={{ marginTop: 'var(--s3)' }}>
          <p className="subsec-title" style={{ marginBottom: 'var(--s2)' }}>{columnLabel(slug)}</p>
          {rows.length === 0 ? (
            <p className="t-muted t-sm">No keywords configured for this column.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  {/* Sprint 5 client feedback 2026-07-11 (Iter4 G3, D5)
                      — Source Doc + Page columns COMBINED into a
                      single Source column; value rendered as
                      "Investment Memo p.32". Status column tooltip
                      preserves newline formatting so its text renders
                      as three legible paragraphs (was one wrapping
                      sentence). */}
                  <tr>
                    <th>Characteristic</th>
                    <th>Evidence</th>
                    <th>Source</th>
                    <th data-tip={_STATUS_TOOLTIP} style={{ whiteSpace: 'pre-line' }}>
                      Status <span style={{ opacity: 0.5, cursor: 'help' }}>ⓘ</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    // Sprint 5 client feedback 2026-07-10 (DD Step2 §6.6)
                    // — new "Not Available" status (BE-emitted when
                    // no chunk cleared the evidence floor) is rendered
                    // with the neutral risk-unknown chip so it reads
                    // as informational rather than a failure.
                    const sCls =
                      row.status === 'Matched' || row.status === 'Supported' ? 'risk-low'
                      : row.status === 'Partial' ? 'risk-medium'
                      : row.status === 'Not Available' ? 'risk-unknown'
                      : row.status === 'Not Matched' ? 'risk-high'
                      : 'risk-high';
                    // Sprint 5 client feedback 2026-07-10 (DD Step2 §6.6/§6.7
                    // + Iter2 §6) — suppress the Source Doc + Page cells
                    // only when the row truly has no citation
                    // (source_document AND source_page both empty).
                    // "Not Available" with a populated source (LLM-empty
                    // path where chunks WERE retrieved but the LLM
                    // couldn't judge) still cites its source so the
                    // analyst can read the excerpt manually.
                    const _isNoEvidence = !row.source_document && row.source_page == null;
                    return (
                      <tr key={`${slug}-${i}`}>
                        <td className="td-name">{_sentenceCaseChar(row.characteristic)}</td>
                        <td className="t-sm">
                          {/* Sprint 5 client feedback 2026-07-11 (Iter6 #2)
                              — every Evidence cell now follows the same
                              two-block layout for consistent typography
                              + spacing + View button placement:
                                (1) small uppercase label ("Excerpt" or
                                    "AI Analysis") so the analyst can
                                    tell the source at a glance without
                                    styling the body text differently.
                                (2) plain-text body (no italics, no
                                    <em>quotes<em>, no bold) rendered
                                    through the same TruncatedCell so
                                    the View button sits in the exact
                                    same spot on every row.
                              Extracted evidence carries the "Excerpt"
                              label; AI-only rows carry "AI Analysis".
                              When both exist, both blocks stack. */}
                          {(() => {
                            const hasExcerpt = Boolean(row.evidence);
                            const hasRationale = Boolean(row.rationale);
                            if (!hasExcerpt && !hasRationale) {
                              return <span className="t-muted">No relevant information found.</span>;
                            }
                            const _labelStyle = {
                              fontSize: '0.62rem',
                              textTransform: 'uppercase' as const,
                              letterSpacing: '0.06em',
                              fontWeight: 700,
                              color: 'var(--text-sec)',
                              marginBottom: 2,
                              display: 'block',
                            };
                            const _bodyStyle = {
                              fontSize: '0.86rem',
                              lineHeight: 1.5,
                              fontWeight: 400,
                            };
                            const _modalText = [
                              hasExcerpt ? `Excerpt:\n${row.evidence}` : null,
                              hasRationale ? `AI Analysis:\n${row.rationale}` : null,
                            ].filter(Boolean).join('\n\n');
                            return (
                              <TruncatedCell title="Evidence" text={_modalText}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {hasExcerpt && (
                                    <div>
                                      <span style={_labelStyle}>Excerpt</span>
                                      <div style={_bodyStyle}>{row.evidence}</div>
                                    </div>
                                  )}
                                  {hasRationale && (
                                    <div>
                                      <span style={_labelStyle}>AI Analysis</span>
                                      {/* Sprint 5 client feedback 2026-07-11 (Format DD §6.11
                                          counter-feedback) — the earlier FE regex tried to
                                          derive bullet reasons from the prose rationale and
                                          produced sentence fragments ("hands-on portfolio
                                          oversight", "board control,", "value-creation
                                          initiatives") that read as noise. The BE now emits
                                          a structured `reasons` array (3-5 short items) so
                                          the FE can render clean bullets when present, or
                                          fall back to prose when the array is empty. */}
                                      {(() => {
                                        const _rat = (row.rationale ?? '').trim();
                                        const _rawReasons = (row as { reasons?: unknown }).reasons;
                                        const _reasons: string[] = Array.isArray(_rawReasons)
                                          ? (_rawReasons.filter(
                                              (r) => typeof r === 'string' && r.trim().length > 0,
                                            ) as string[])
                                          : [];
                                        if (_reasons.length >= 2) {
                                          const _st = row.status ?? '';
                                          return (
                                            <div style={_bodyStyle}>
                                              <div><strong>Status</strong> — {_st}</div>
                                              <div style={{ marginTop: 4 }}><strong>Reason</strong></div>
                                              <ul style={{ margin: '4px 0 0 0', paddingLeft: '1.15em' }}>
                                                {_reasons.map((b, bi) => (
                                                  <li key={bi} style={{ marginBottom: 2 }}>
                                                    <FormattedText text={b} inline />
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          );
                                        }
                                        return (
                                          <div style={_bodyStyle}>
                                            <FormattedText text={_rat} inline />
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              </TruncatedCell>
                            );
                          })()}
                        </td>
                        <td className="t-xs">
                          {/* Iter6 #1 — canonical SourceChip. */}
                          {_isNoEvidence
                            ? <span className="t-muted">—</span>
                            : <SourceChip doc={row.source_document ?? null} page={row.source_page} />}
                        </td>
                        <td>
                          {/* Sprint 5 client feedback 2026-07-11 (Iter4b §6)
                              — Status per-cell tooltip must be the AI's
                              REASON for this verdict, distinct from the
                              excerpt in the Evidence column. When the BE
                              rationale looks like it just quotes the
                              excerpt, synthesise a distinct tooltip
                              anchored on the status word so the analyst
                              sees "Marked X because…" instead of the
                              excerpt again. Otherwise pass the rationale
                              through with a verdict-anchored prefix. */}
                          {(() => {
                            const _rat = (row.rationale ?? '').trim();
                            const _ev = (row.evidence ?? '').trim();
                            const _looksLikeEvidence = (() => {
                              if (!_rat || !_ev) return false;
                              const nr = _rat.replace(/\s+/g, ' ').toLowerCase();
                              const ne = _ev.replace(/\s+/g, ' ').toLowerCase();
                              if (nr.length < 20) return false;
                              if (ne.includes(nr) || nr.includes(ne)) return true;
                              const head = nr.slice(0, Math.min(60, nr.length));
                              return ne.includes(head);
                            })();
                            let tip: string;
                            if (!_rat) {
                              tip = `Marked ${row.status}: the AI did not attach a specific rationale for this row.`;
                            } else if (_looksLikeEvidence) {
                              tip = `Marked ${row.status}: the AI derived this verdict from the excerpt shown in the Evidence column; a distinct reasoning statement was not produced. Reviewer should judge whether the excerpt actually supports the ${row.status} verdict.`;
                            } else {
                              const prefix = /^(marked|assigned|categori[sz]ed|concluded|classified)\b/i.test(_rat)
                                ? ''
                                : `Marked ${row.status} — Why: `;
                              tip = `${prefix}${_rat}`;
                            }
                            return (
                              <span
                                className={sCls}
                                style={{ cursor: 'help' }}
                                data-tip={cleanTooltip(tip)}
                              >
                                {row.status}
                              </span>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );

      built.push({
        id: 'section-signals',
        num: '06',
        title: 'Other Characteristics',
        body: (
          <>
            {Object.entries(signalsByColumn).map(([slug, rows]) =>
              renderColumnTable(slug, rows)
            )}
          </>
        ),
      });
    }

    // Section 06 — Key Assumptions (Sprint 3 US2; FR-REPORT-017)
    // Reads the enriched array verify_assumptions writes back into the
    // attribute. Pre-classified per row (variance_pct, assessment, flag)
    // so this renderer just paints.
    //
    // The section ALWAYS renders. If the engine produced rows, paint
    // them as a table. If not (LLM couldn't extract a consultant
    // assumptions table from the documents), show an explicit
    // empty-state instead of silently hiding the section — the analyst
    // needs to see that the engine ran and what it concluded.
    const assumptionsRows = groups?.assumptions ?? null;
    {
      built.push({
        id: 'section-assumptions',
        num: '06',
        title: 'Key Assumptions',
        body: assumptionsRows && assumptionsRows.length > 0 ? (
          // Sprint 5 client feedback 2026-07-10 (DD Step2 §7 + Iter1 §7)
          // — Consultant + Stated cols dropped; Source P.→Page. Status
          // tooltip vocab corrected to match what rows actually display
          // (Matched/Partial/Not Matched/Not Supported when the LLM
          // verdict populated row.status; In-Range/Watch/Aggressive/
          // Conservative when only variance-band assessment). Per-row
          // Status cell also carries a tooltip surfacing the LLM
          // rationale. Assumption column shows a title + explicit
          // "View" button so the analyst sees there's more detail
          // available (Iter1 asked for the button to always be
          // visible like §6 Evidence).
          <AssumptionsTable rows={assumptionsRows} />
        ) : (
          <div className="empty-state" style={{ padding: 'var(--s5)', textAlign: 'center', border: '1px dashed var(--c-border)', borderRadius: 'var(--r-md)' }}>
            <p className="t-muted">
              No consultant assumptions table was extracted from the submitted documents.
            </p>
            <p className="t-xs t-muted" style={{ marginTop: 'var(--s2)' }}>
              The verification engine ran but could not parse a structured assumptions table. See Section "Flags &amp; Action Items" for the related Medium flag — manual review is required to confirm consultant assumptions.
            </p>
          </div>
        ),
      });
    }

    // Section 07 — Calculations Verification (Sprint 3 US2; FR-REPORT-018)
    // validate_calculations produces one row per YAML validation_rules
    // entry with status Verified / Review / Failed / Skipped.
    const calcRows = groups?.calculation_verifications ?? null;
    if (calcRows && calcRows.length > 0) {
      built.push({
        id: 'section-calc-verification',
        num: '07',
        // Sprint 5 client feedback 2026-07-10 (DD Step2 §8.1) —
        // renamed at client's request.
        title: 'Financial Parameter Validations',
        body: (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                {/* Sprint 5 client feedback 2026-07-10 (DD Step2 §8.2/§8.5)
                    — Inputs + Formula columns dropped (their content
                    now lives inside How Validated in business
                    language); "Source p." renamed to "Page". */}
                {/* Sprint 5 client feedback 2026-07-11 (Iter4b) —
                    Source Doc + Page merged into single Source column
                    matching the global "one Source column" rule. */}
                <tr>
                  <th>Calculation</th>
                  <th>Stated</th>
                  <th>Computed</th>
                  <th>Variance %</th>
                  <th>Status</th>
                  <th>How Validated</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {calcRows.map((row, i) => {
                  const sCls =
                    row.status === 'Verified' ? 'risk-minimal'
                    : row.status === 'Review' ? 'risk-medium'
                    : row.status === 'Failed' ? 'risk-high'
                    : row.status === 'Computed' ? 'risk-low'
                    : row.status === 'Self-Consistency' ? 'risk-medium'
                    : '';
                  return (
                    <tr key={`calc-${i}`}>
                      <td className="td-name">{row.calc_name}</td>
                      <td>
                        {/* Sprint 5 client feedback 2026-07-11 (Iter3 §8.1)
                            — client asked to drop the raw source snippet
                            beneath the stated value (e.g. "EBITDA (157)
                            (177) (75)…" / "XIRR / 29%"). The value alone
                            is enough; the source doc + page are already
                            available in dedicated columns and the full
                            source context lives in the Validation
                            Method popup. */}
                        {row.stated_value != null ? <strong>{row.stated_value}</strong> : <span className="t-muted">—</span>}
                      </td>
                      <td>{row.computed_value != null ? row.computed_value.toFixed(2) : <span className="t-muted">—</span>}</td>
                      <td>{row.variance_pct != null ? `${row.variance_pct.toFixed(2)}%` : <span className="t-muted">—</span>}</td>
                      <td>
                        {sCls ? <span className={sCls}>{row.status}</span> : <StatusChip value={row.status} />}
                        {row.status === 'Skipped' && row.missing_inputs && row.missing_inputs.length > 0 && (
                          <div className="t-xs t-muted" style={{ marginTop: 2 }}>
                            {/* Sprint 5 client feedback 2026-07-11 (Iter7 #3)
                                — humanise raw attribute keys ("entry_ev_usd_m")
                                to business labels ("Enterprise Value (USD Million)")
                                per the same map the BE _business_label uses. */}
                            missing: {row.missing_inputs.map((k) => {
                              const _labels: Record<string, string> = {
                                revenue_usd_m: 'Revenue (USD Million)',
                                ebitda_usd_m: 'EBITDA (USD Million)',
                                ebitda_margin_pct: 'EBITDA Margin (%)',
                                ev_ebitda_multiple: 'EV / EBITDA Multiple',
                                entry_ev_usd_m: 'Enterprise Value (USD Million)',
                                cash_flow_schedule: 'Cash Flow Schedule',
                                irr_pct: 'IRR (%)',
                                xirr_pct: 'XIRR (%)',
                                holding_period: 'Holding Period (Years)',
                                deal_size: 'Deal Size (USD Million)',
                              };
                              return _labels[k] ?? k;
                            }).join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="t-xs" style={{ maxWidth: 280 }}>
                        {row.how_validated ? (
                          <TruncatedCell title="Validation Method" text={row.how_validated} />
                        ) : <span className="t-muted">—</span>}
                      </td>
                      <td className="t-xs">
                        {/* Iter6 #1 — unified Source chip. */}
                        <SourceChip doc={row.source_doc ?? null} page={row.source_page} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ),
      });
    }

    // Section 08 — Standards Compliance (Sprint 3 US3; FR-REPORT-019)
    // confirm_standards.py produces one row per YAML-declared standard,
    // each with an LLM-judged status (Compliant / Watch / Non-Compliant
    // / Not Applicable) + evidence excerpt + rationale.
    const standardsRows = groups?.standards_compliance ?? null;
    if (standardsRows && standardsRows.length > 0) {
      built.push({
        id: 'section-standards',
        num: '08',
        title: 'Standards Compliance',
        body: (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {/* Iter4b — Source Doc + Source p. merged into
                      single Source column per the global rule. */}
                  {['Standard', 'Authority', 'Applicable', 'Implemented', 'Status', 'Evidence', 'Deficiencies / Follow-up', 'Source'].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {standardsRows.map((row, i) => {
                  const sCls =
                    row.status === 'Compliant' ? 'risk-low'
                    : row.status === 'Watch' ? 'risk-medium'
                    : row.status === 'Non-Compliant' ? 'risk-high'
                    : '';
                  return (
                    <tr key={`std-${i}`}>
                      <td className="td-name">{row.standard}</td>
                      <td className="t-xs">{row.issuing_authority ?? <span className="t-muted">—</span>}</td>
                      <td className="t-xs">{row.applicable ?? <span className="t-muted">—</span>}</td>
                      <td className="t-xs">{row.implemented ?? <span className="t-muted">—</span>}</td>
                      <td>
                        {/* Sprint 5 client feedback 2026-07-07 (UI Issues #7) —
                            §16 Standards Compliance status column: neutral
                            StatusChip fallback for unmapped values. */}
                        {sCls ? <span className={sCls}>{row.status}</span> : <StatusChip value={row.status} />}
                      </td>
                      <td className="t-sm" style={{ maxWidth: 280 }}>
                        {/* FE Ticket 1 2026-07-07 — clamp Evidence +
                            Rationale under a shared TruncatedCell. */}
                        {row.evidence_text || row.rationale ? (
                          <TruncatedCell
                            title="Evidence"
                            text={[row.evidence_text, row.rationale].filter(Boolean).join('\n\n')}
                          >
                            {row.evidence_text ? (
                              <>
                                <em>"{row.evidence_text}"</em>
                                {row.rationale && (
                                  <div className="t-xs t-muted" style={{ marginTop: 4 }}>
                                    <FormattedText text={row.rationale} />
                                  </div>
                                )}
                              </>
                            ) : (
                              <FormattedText text={row.rationale ?? ''} className="t-muted" />
                            )}
                          </TruncatedCell>
                        ) : (
                          <span className="t-muted">—</span>
                        )}
                      </td>
                      <td className="t-xs" style={{ maxWidth: 240 }}>
                        {/* FE Ticket 1 2026-07-07 — Gaps + Next combined
                            under one TruncatedCell so the row height
                            stays consistent. */}
                        {row.deficiencies || row.follow_up_actions ? (
                          <TruncatedCell
                            title="Deficiencies / Follow-up"
                            text={[
                              row.deficiencies ? `Gaps: ${row.deficiencies}` : '',
                              row.follow_up_actions ? `Next: ${row.follow_up_actions}` : '',
                            ].filter(Boolean).join('\n\n')}
                          >
                            <>
                              {row.deficiencies && (
                                <div style={{ marginBottom: 4 }}>
                                  <strong>Gaps:</strong> {row.deficiencies}
                                </div>
                              )}
                              {row.follow_up_actions && (
                                <div>
                                  <strong>Next:</strong> {row.follow_up_actions}
                                </div>
                              )}
                            </>
                          </TruncatedCell>
                        ) : (
                          <span className="t-muted">—</span>
                        )}
                      </td>
                      <td className="t-xs">
                        {/* Iter6 #1 — unified Source chip. */}
                        <SourceChip doc={row.source_document ?? null} page={row.source_page} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ),
      });
    }

    // Section 06 — Holding Period (was 07; Other Characteristics is
    // deferred to a future sprint so we collapse the numbering here).
    if (holdingPeriod) {
      built.push({
        id: 'section-06',
        num: '06',
        title: 'Holding Period',
        body: (
          <>
          <div className="meta-grid">
            <div className="meta-row">
              <span className="t-muted t-sm">Value</span>
              <span className="t-sm">{holdingPeriod.value ?? '—'}</span>
            </div>
            <div className="meta-row">
              <span className="t-muted t-sm">Type</span>
              <span className="t-sm"><span className="badge badge-neutral" style={{ fontSize: '0.7rem' }}>{holdingPeriod.value_type}</span></span>
            </div>
            {holdingPeriod.derivation_detail?.inputs?.map((inp) => {
              const label = inp.field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
              return (
                <div key={inp.field} className="meta-row">
                  <span className="t-muted t-sm">{label}</span>
                  <span className="t-sm">
                    {inp.value != null ? formatFreeDate(inp.value) : <span className="t-muted">—</span>}
                    {(inp.source_document || inp.source_page != null) && (
                      <span className="t-xs t-muted" style={{ marginLeft: 8 }}>
                        {inp.source_document && <>· {inp.source_document}</>}
                        {inp.source_page != null && <> · p.{inp.source_page}</>}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
            {holdingPeriod.derivation_detail && (
              <div className="meta-row">
                <span className="t-muted t-sm">Derivation</span>
                <span className="t-sm t-mono">{holdingPeriod.derivation_detail.formula} = {holdingPeriod.derivation_detail.result}</span>
              </div>
            )}
          </div>
          <SourceLine attr={holdingPeriod} />
          </>
        ),
      });
    }

    // Section 07 — Flags & Action Items (was 08; renumbered after the
    // Other Characteristics drop).
    // Sprint 5 client feedback 2026-07-10 (DD Step2 §11) — column set
    // and content reworked per client direction:
    //   §11.1 Watch severity → Medium (normalised at render)
    //   §11.2 ID column dropped; Finding Name column derived from
    //         attribute_key / flag_id pattern
    //   §11.3 IRR + CALC same-underlying-issue findings deduped
    //   §11.4 Source column replaced with Section column that names
    //         the originating report section
    //   §11.5 Overall Risk Rating injected as the top action item
    //   §11.6 Responsible column dropped
    if (analysis.flags && analysis.flags.length > 0) {
      /* Sprint 5 client feedback 2026-07-12 (Iter9 #2) — new 4-tier
       * severity model applied to §11 (was §07 in this file's naming):
       *
       *   High → Medium → Low → Recommendation
       *
       * Critical is retired at the severity level and folds into
       * High per the client spec. Recommendation is a new tier below
       * Low, populated via a two-step AI pipeline (classify Low →
       * re-check evidence → confirm non-material → downgrade). A
       * downgraded Recommendation MUST carry a rationale in the
       * description; otherwise it renders as Low. */
      const _normaliseSev = (s: string | null | undefined): string => {
        const t = (s ?? '').trim();
        if (t === 'Critical' || t === 'Fail' || t === 'Reject' || t === 'Severe') return 'High';
        if (t === 'Watch') return 'Medium';
        if (t === 'Recommendation' || t === 'Opportunity' || t === 'Info') return 'Recommendation';
        return t || 'Medium';
      };
      // §11.2 — friendly Finding Name derivation. Prefer the flag's
      // attribute_key (converted from snake_case) when present; else
      // pattern-match on flag_id prefix; else fall back to the first
      // clause of the description.
      const _ATTR_TO_NAME: Record<string, string> = {
        irr_pct: 'IRR',
        xirr_pct: 'XIRR',
        entry_ev_usd_m: 'Enterprise Value',
        revenue_usd_m: 'Revenue',
        ebitda_usd_m: 'EBITDA',
        ebitda_margin_pct: 'EBITDA Margin',
        ev_ebitda_multiple: 'EV / EBITDA Multiple',
        deal_size: 'Deal Size',
        holding_period: 'Holding Period',
        cash_flow_schedule: 'Cash Flow Schedule',
        lead_investor: 'Lead Investor',
      };
      const _findingName = (flag: typeof analysis.flags[0]): string => {
        const ak = (flag as unknown as { attribute_key?: string }).attribute_key;
        if (ak && _ATTR_TO_NAME[ak]) {
          /* Iter9 #2 — Critical folded into High, so a single check
           * covers both legacy Critical rows and current High rows. */
          const sev = _normaliseSev(flag.severity);
          const suffix = sev === 'High'
            ? 'Not Found'
            : (flag.flag_id ?? '').includes('CALC') ? 'Calculation Variance'
            : 'Review';
          return `${_ATTR_TO_NAME[ak]} ${suffix}`;
        }
        if (ak) {
          return ak.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
        }
        const fid = flag.flag_id ?? '';
        if (fid.startsWith('DD-IRR-') || fid.startsWith('DD-CALC-')) {
          return 'Financial Calculation Review';
        }
        if (fid.startsWith('DD-NOTFOUND-')) return 'Data Not Found';
        if (fid.startsWith('SPA-CLAUSE-')) return 'SPA Clause Review';
        if (fid.startsWith('SPA-SEC-')) return 'SPA Section Review';
        const desc = flag.description ?? '';
        return desc.split(/[.:—]/)[0].slice(0, 60) || 'Finding';
      };
      // Sprint 5 client feedback 2026-07-10 (Iter1 §11) — section
      // routing was misclassifying some rows. Reworked to key off
      // attribute_key FIRST (most reliable), then flag_id prefix,
      // then description keyword. All targets are DD-report sections
      // (the earlier reference to "Section 10 — Legal Assessment"
      // was a Risk-report section that doesn't exist in the DD
      // report, so SPA-flag routing now points to Section 08).
      const _sectionRef = (flag: typeof analysis.flags[0]): string => {
        const ak = ((flag as unknown as { attribute_key?: string }).attribute_key ?? '').toLowerCase();
        const fid = (flag.flag_id ?? '').toUpperCase();
        const desc = (flag.description ?? '').toLowerCase();
        // Calculation / IRR / XIRR / EBITDA / EV / Enterprise Value
        if (ak === 'irr_pct' || ak === 'xirr_pct' || ak === 'ebitda_usd_m' || ak === 'ebitda_margin_pct'
          || ak === 'ev_ebitda_multiple' || ak === 'entry_ev_usd_m' || ak === 'cash_flow_schedule'
          || fid.includes('CALC') || fid.includes('IRR') || fid.includes('XIRR')
          || fid.includes('EBITDA') || fid.includes('EV-')) {
          return 'Section 08 — Financial Parameter Validations';
        }
        // Missing-metric findings from validate_calculations / deal_size
        if (fid.includes('NOTFOUND') || fid.includes('NOT-FOUND') || fid.startsWith('DD-BENCH-')
          || ak === 'deal_size' || ak === 'holding_period' || ak === 'revenue_usd_m'
          || ak === 'lead_investor') {
          return 'Section 04 — Financial Parameters & Benchmark Classification';
        }
        // Assumption-verification flags
        if (fid.includes('ASSUMPTION') || desc.includes('assumption')) {
          return 'Section 07 — Key Assumptions';
        }
        // Characteristic-signal flags
        if (fid.includes('SIGNAL') || fid.includes('CHAR-')) {
          return 'Section 06 — Other Characteristics';
        }
        // SPA / legal — DD report doesn't itself have a Legal section,
        // so route to §04 where SPA cross-reference clauses surface.
        if (fid.startsWith('SPA-') || desc.includes('spa clause') || desc.includes('governing law')) {
          return 'Section 04 — Financial Parameters & Benchmark Classification';
        }
        // Risk-profile fallback for anything else
        return 'Section 05 — Risk Profile';
      };
      // §11.3 — dedupe rows that reference the same underlying calc.
      // Key = attribute_key + severity. IRR + CALC flags for the same
      // metric collapse to one row; description of the higher-severity
      // survivor wins.
      const _dedupKey = (flag: typeof analysis.flags[0]): string => {
        const ak = (flag as unknown as { attribute_key?: string }).attribute_key ?? '';
        const fid = flag.flag_id ?? '';
        // Group both IRR-family flags (attribute_key=irr_pct + any
        // flag_id starting DD-IRR- or DD-CALC- that ends up on irr_pct)
        // together.
        if (ak === 'irr_pct' || fid.startsWith('DD-IRR-')) return 'g:irr';
        if (ak === 'xirr_pct') return 'g:xirr';
        return `g:${ak || fid}`;
      };
      /* Iter9 #2 — new severity rank: Critical folds into High so
       * both share rank 4; Recommendation slots below Low at 0.5. */
      const _sevRank: Record<string, number> = { Critical: 4, High: 4, Medium: 3, Low: 2, Recommendation: 1 };
      const _dedupedFlags = (() => {
        const byGroup = new Map<string, typeof analysis.flags[0]>();
        for (const f of analysis.flags) {
          const key = _dedupKey(f);
          const prev = byGroup.get(key);
          if (!prev || (_sevRank[_normaliseSev(f.severity)] ?? 0) > (_sevRank[_normaliseSev(prev.severity)] ?? 0)) {
            byGroup.set(key, f);
          }
        }
        return Array.from(byGroup.values());
      })();
      // Sprint 5 client feedback 2026-07-10 (Step2 §11.5 + Iter1 §11 +
      // Iter2 §11) — the Overall Risk Rating row MUST match what §5
      // Risk Profile shows in its "Overall Investment Risk" panel.
      // §5 uses summary.overall_rating (BE-computed) with a fallback
      // to _compositeRiskVerdict of risk_profile.table_rows, then
      // collapses Critical → High via _toBenchmarkTier. Replicate the
      // exact same derivation here so §5 and §11 tell the same story.
      const _rpSummary = (
        (analysis as unknown as Record<string, unknown>)['risk_profile'] as
          | { summary?: { overall_rating?: string | null } }
          | undefined
      )?.summary ?? null;
      const _rpRows: Record<string, unknown>[] =
        riskProfile
          ? ((riskProfile.table_rows ?? []) as Record<string, unknown>[]).filter(
              (r) => r != null && typeof r === 'object' && !Array.isArray(r),
            )
          : [];
      const _overallRating: 'High' | 'Medium' | 'Low' | null =
        (_rpSummary?.overall_rating as 'High' | 'Medium' | 'Low' | undefined)
        ?? (_rpRows.length > 0 ? _toBenchmarkTier(_compositeRiskVerdict(_rpRows)) : null);
      built.push({
        id: 'section-07',
        num: '07',
        title: 'Flags & Action Items',
        body: (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                {/* Sprint 5 client feedback 2026-07-12 (Iter9 #2) —
                    Status column removed. It always showed "Open" for
                    every row, adding no analyst value while consuming
                    horizontal space that Description could use. Final
                    column set is now Finding Name | Severity |
                    Description | Section per the iter9 spec. */}
                <tr>
                  <th>Finding Name</th>
                  <th>Severity</th>
                  <th>Description</th>
                  <th>Section</th>
                </tr>
              </thead>
              <tbody>
                {_overallRating && (
                  <tr key="overall-risk-rating">
                    <td className="td-name">Overall Investment Risk Rating</td>
                    <td>
                      <span className={
                        _overallRating === 'High' ? 'risk-high'
                        : _overallRating === 'Medium' ? 'risk-medium'
                        : 'risk-low'
                      }>{_overallRating}</span>
                    </td>
                    <td style={{ maxWidth: 360 }}>
                      Consolidated risk assessment across all identified findings, benchmark evaluation, and mitigation effectiveness. Refer to Section 05 for the per-risk breakdown.
                    </td>
                    <td className="t-xs">Section 05 — Risk Profile</td>
                  </tr>
                )}
                {_dedupedFlags.map((flag, i) => {
                  const sev = _normaliseSev(flag.severity);
                  /* Iter9 #2 — colour spec: High=red, Medium=amber,
                   * Low=green, Recommendation=blue. Critical folds
                   * into High before display. */
                  const sevCls = sev === 'High' ? 'risk-high'
                    : sev === 'Medium' ? 'risk-medium'
                    : sev === 'Recommendation' ? 'risk-recommendation'
                    : 'risk-low';
                  return (
                    <tr key={`f-${i}`}>
                      <td className="td-name">{_findingName(flag)}</td>
                      <td><span className={sevCls}>{sev}</span></td>
                      <td style={{ maxWidth: 360 }}>
                        {flag.description
                          ? <TruncatedCell title="Finding Description" text={flag.description} />
                          : <span className="t-muted">—</span>}
                      </td>
                      <td className="t-xs">{_sectionRef(flag)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ),
      });
    }

    // Section 08 — Document Traceability (was 09; renumbered after the
    // Other Characteristics drop).
    // Sprint 5 client feedback 2026-07-10 (DD Step2 §12) — column set
    // simplified per client direction:
    //   §12.1 Internal document ID (DD-2026-XXXX) removed from below
    //         the filename.
    //   §12.2 Status column dropped (all listed docs are successfully
    //         processed by definition of appearing here).
    //   §12.3 Date Received now shows both date + time in the
    //         canonical "09 Jul 2026, 10:45 AM" format.
    built.push({
      id: 'section-08',
      num: '08',
      title: 'Document Traceability',
      body: (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Type</th>
                <th>Date Received</th>
              </tr>
            </thead>
            <tbody>
              {status.documents.map((d) => {
                const dateStr = status.created_at
                  ? new Date(status.created_at).toLocaleString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', hour12: true,
                    }).replace(',', ',')
                  : '—';
                return (
                  <tr key={d.job_id}>
                    <td className="td-name" style={{ maxWidth: 360, wordBreak: 'break-word' }}>
                      {d.filename}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {SUBTYPE_LABEL[d.document_subtype ?? ''] ?? d.document_subtype ?? '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }} className="t-xs">{dateStr}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ),
    });


    // Re-stamp section numbers by position so the TOC stays contiguous
    // when a conditional section is skipped (e.g. Flags only renders
    // if there are flags). Without this, a missing Section 07 made the
    // TOC read ".. 06 .. 08" with a visible gap.
    built.forEach((section, idx) => {
      section.num = String(idx + 1).padStart(2, '0');
    });

    return built;
  }, [analysis, status]);

  if (!assessmentId) return null;

  const moduleClass = status?.module_type === 'SPA' ? 'module-spa' : 'module-dd';
  const uploadPath = status?.module_type === 'SPA' ? '/spa/upload' : '/dd/upload';

  if (isLoading) {
    return (
      <div className={moduleClass} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner spinner-lg" />
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className={moduleClass}>
        <div className="page-body">
          <div className="alert alert-fail" role="alert">{error ?? 'Report not found.'}</div>
          <button className="btn btn-secondary" style={{ marginTop: 'var(--s3)' }} onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    /* The status loaded fine but the analysis blob is null. Differentiate
       the message by status so the analyst knows WHY there's nothing to
       show — "still running" vs "failed" vs "completed but report missing"
       are three very different situations and a single generic placeholder
       hid them all behind the same line. */
    const st = String(status.status || '').toLowerCase();
    const isRunning = ['pending', 'ingesting', 'analysing', 'analyzing', 'in_progress', 'queued'].includes(st);
    const isFailed = ['failed', 'cancelled', 'canceled', 'error'].includes(st);
    const isCompleted = ['completed', 'complete', 'done'].includes(st);

    let variant: 'warn' | 'fail' | 'info' = 'warn';
    let title = '';
    let body = '';
    let action: { label: string; href: string } | null = null;

    if (isRunning) {
      variant = 'info';
      title = 'Analysis still in progress';
      body = `Status: ${status.status}. The report will appear here once the worker finishes — check back in a few minutes, or open the Processing page for live progress.`;
      action = { label: 'Open Processing Page →', href: `/processing/${assessmentId}` };
    } else if (isFailed) {
      variant = 'fail';
      title = `Analysis ${st}`;
      body = 'The worker did not produce a report. Open the Processing page to see the failing step, or re-upload the documents to retry.';
      action = { label: 'Open Processing Page →', href: `/processing/${assessmentId}` };
    } else if (isCompleted) {
      variant = 'fail';
      title = 'Report data missing despite completed status';
      body = `Assessment status is "${status.status}" but no analysis payload was returned. This usually means the WorkflowJobResult row was not persisted at the end of the run. Re-running the analysis from the Processing page is the safest recovery.`;
      action = { label: 'Open Processing Page →', href: `/processing/${assessmentId}` };
    } else {
      title = "Analysis hasn't completed yet for this assessment";
      body = `Status: ${status.status || 'unknown'}. The report will appear here once analysis finishes.`;
      action = { label: '← Back to Assessment', href: `/assessments/${assessmentId}` };
    }

    return (
      <div className={moduleClass}>
        <div className="page-body">
          <div className={`alert alert-${variant}`}>
            <strong>{title}</strong>
            <div style={{ marginTop: 6, fontWeight: 400 }}>{body}</div>
          </div>
          <div style={{ marginTop: 'var(--s3)', display: 'flex', gap: 'var(--s2)' }}>
            {action && (
              <button
                className="btn btn-primary"
                onClick={() => navigate(action.href)}
              >
                {action.label}
              </button>
            )}
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>
              ← Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const primarySubtype = status.documents[0]?.document_subtype;
  /* Sprint 5 client feedback 2026-07-12 (Iter9 #3) — header flag
   * counter: SPA emits into attribute_groups.issues instead of
   * analysis.flags, so pull from both sources and recompute using
   * the new severity vocab (Critical folds into High; other tiers
   * pass through). Fixes the "flags · N high" chip so SPA reports
   * show a real count instead of hiding at zero. */
  const _spaIssues = (() => {
    if (analysis.workflow_code !== 'SPA') return [] as { severity?: string | null }[];
    const groups = analysis.attribute_groups as { issues?: { severity?: string | null }[] } | undefined;
    return groups?.issues ?? [];
  })();
  const _flagSource: { severity?: string | null }[] = analysis.workflow_code === 'SPA'
    ? _spaIssues
    : (analysis.flags ?? []);
  const flagCount = _flagSource.length;
  const highCount = _flagSource.filter((f) => f.severity === 'High' || f.severity === 'Critical').length;
  const overall = analysis.overall_status ?? 'N/A';

  const processedDuration = (() => {
    if (!analysis.analysis_completed_at || !status.created_at) return null;
    const ms = new Date(analysis.analysis_completed_at).getTime() - new Date(status.created_at).getTime();
    const sec = Math.max(0, Math.round(ms / 1000));
    if (sec < 60) return `${sec} sec`;
    const m = Math.floor(sec / 60);
    const rs = sec % 60;
    return rs === 0 ? `${m} min` : `${m} min ${rs} sec`;
  })();

  return (
    <div className={moduleClass}>
      {/* Sticky top chrome — breadcrumb on the left, "All Reports" +
          "Download PDF" actions on the right. Matches
          mpzesihq-code.html. */}
      <header className="report-topbar">
        <div className="breadcrumb">
          <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>Dashboard</a>
          <span className="bc-sep">›</span>
          <a href={uploadPath} onClick={(e) => { e.preventDefault(); navigate(uploadPath); }}>{status.module_type} Assessments</a>
          <span className="bc-sep">›</span>
          {/* Sprint 5 client feedback 2026-07-11 (Iter4 S5, Iter6d)
              — breadcrumb tail switches from "{ID} Report" to
              "{ProjectName} DD/SPA". Prefer project_name from the
              BE resolver; fall back to deal_name; assessment_id
              only if both are null. */}
          <span className="bc-current">
            {(() => {
              const _label = status.project_name ?? status.deal_name ?? null;
              if (!_label) return `${assessmentId} Report`;
              return `${_label} ${analysis.workflow_code === 'SPA' ? 'SPA' : 'DD'}`;
            })()}
          </span>
        </div>
        <div className="topbar-actions">
          {/* Sprint 5 client feedback 2026-06-25 (Issues/FE doc #2) —
              Back button moved to the FAR RIGHT to match the Risk
              Report action-button order. Order is now:
                [ All Reports ] [ ← Back ]
              (No Download button on DD/SPA — that lives on Risk
              Report only.) */}
          <button className="btn btn-secondary" onClick={() => navigate(uploadPath)} type="button">
            All Reports
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
        </div>
      </header>

      {/* Report info — eyebrow + large title + status pill + metadata row.
          Pattern from mpzesihq-code.html: 4xl deal title with a single
          row of icon+text metadata below. */}
      <div className="module-page-header">
        <div className="report-title-row">
          <div>
            <p className="module-eyebrow">
              {status.module_type} Module{primarySubtype ? ` · ${SUBTYPE_LABEL[primarySubtype] ?? primarySubtype}` : ''}
            </p>
            {/* Sprint 5 client feedback 2026-07-11 (Iter3 SPA #1) —
                SPA report title should read "{Project Name} — SPA"
                instead of the internal SPA-YYYY-NNNN identifier.
                Suffix injects the module type (SPA / DD) so analysts
                can tell reports apart at a glance. When deal_name is
                unavailable, fall back to the assessment ID (existing
                behaviour). */}
            {/* Sprint 5 client feedback 2026-07-11 (Iter4 D1/S1,
                Iter4b, Iter6d) — report title reads "{Project Name}
                DD" or "{Project Name} SPA". Prefer the BE-resolved
                `project_name` (joined from Assessment.project_id →
                Project.project_name), fall back to legacy free-text
                `deal_name`, and only use the raw assessment_id as an
                absolute last resort. The whole title is one uniform
                font / weight / colour. */}
            <h1 className="t-h1">
              {(() => {
                const _projectLabel = status.project_name ?? status.deal_name ?? null;
                if (!_projectLabel) return assessmentId;
                const _suffix = analysis.workflow_code === 'SPA' ? 'SPA' : 'DD';
                return `${_projectLabel} — ${_suffix}`;
              })()}
            </h1>
            <div className="report-meta-row">
              {/* Sprint 5 client feedback 2026-07-11 (Iter3 SPA #4) —
                  Recommendation badge (Reject / Approve / Conditional)
                  is the single most important signal in the report
                  header. Bump size, weight, and give Reject the fail
                  colour treatment so it stands out from surrounding
                  metadata. Applies to both DD and SPA. */}
              <span
                className={`badge ${STATUS_BADGES[overall] ?? 'badge-neutral'}`}
                style={{
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  padding: '6px 14px',
                  letterSpacing: '0.02em',
                }}
              >
                <span className="pulse-dot" />
                {overall}
              </span>
              <span>
                {/* Sprint 5 client feedback 2026-07-11 (Iter4 D2/S2)
                    — header date now shows both date + time so
                    reviewers can distinguish multiple same-day runs
                    on the same deal. */}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                {formatDateWithTime(analysis.analysis_completed_at ?? status.created_at)}
              </span>
              {/* Sprint 5 client feedback 2026-07-11 (Iter4 D3/S3) —
                  Job ID chip removed from the report header. The
                  assessment_id still appears in the breadcrumb for
                  navigation continuity but is no longer surfaced as
                  a primary header attribute. */}
              {status.analyst_name && (
                <span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  Analyst: {status.analyst_name}
                </span>
              )}
              {flagCount > 0 && (
                <span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                  {flagCount} flag{flagCount !== 1 ? 's' : ''}{highCount > 0 ? ` · ${highCount} high` : ''}
                </span>
              )}
              {processedDuration && (
                <span style={{ fontStyle: 'italic' }}>Processed in {processedDuration}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="report-layout">
          {/* Sticky TOC */}
          <aside className="report-toc">
            <span className="report-toc-label">Contents</span>
            <nav>
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className={`report-toc-item${activeSection === s.id ? ' active' : ''}`}
                  onClick={(e) => { e.preventDefault(); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); setActiveSection(s.id); }}
                >
                  {s.num}. {s.title}
                </a>
              ))}
            </nav>
            {/* Sprint 5 client feedback 2026-06-25 — DD + SPA report
                sticky-TOC download buttons removed alongside the
                header download buttons. All download UI consolidated
                onto the Risk Report page (the final audit
                deliverable). The TOC card now ends with the nav
                list; no trailing button. */}
          </aside>

          {/* Sections */}
          <div className="report-content">
            {sections.map((s) => (
              <section key={s.id} id={s.id} className="report-section">
                <div className="section-head">
                  <span className="section-eyebrow">Section {s.num}</span>
                  <h2 className="section-title">{s.title}</h2>
                </div>
                <div>{s.body}</div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
